import termsMarkdown from '../legal/TERMS_OF_SERVICE.md?raw';
import privacyMarkdown from '../legal/PRIVACY_POLICY.md?raw';
import { compactDisplayUrl, findEmbeddedUrlPreview, prepareMessageUrlPresentation, urlsReferenceSamePage } from './url-preview.js';

// ---------------------------------------------------------------------------
// Imports from shared modules and platform adapter
// ---------------------------------------------------------------------------
/*
import { escapeHtml } from './utils.js';
import { createAccount } from './account.js';
import { loadChannels, setupIntersectionObserver, createMessageCard } from './feed.js';
import {
  API_BASE_URL,
  apiFetchFeed,
  apiFetchMessage,
  apiCheckAuthStatus,
  apiLoadProfile,
  apiLogin,
  apiRegister,
  apiActivate,
  apiLogout,
  apiSavePreferences,
  apiUpvote,
  apiDownvote,
  apiReport,
  apiSearch,
  apiLinkPubkeyInit,
  apiLinkPubkeyVerify,
  apiFollow,
  apiUnfollow,
  WebSocketClient
} from './platform.js';
*/


/**
 * web/src/platform.js
 *
 * Web-platform adapter for Satlas.
 *
 * Wraps all web-specific concerns:
 *   - Backend API base URL
 *   - Authentication (session-cookie based, email/password + activation)
 *   - Feed / search API calls
 *   - User preference persistence
 *   - Voting and reporting
 *   - Public-key linking (pubkey ↔ Satlas account)
 *
 * Imported by web/src/app.js.
 * Not used by Electron or the Chrome Extension.
 */

// ---------------------------------------------------------------------------
// API base URL
// The __API_URL__ token is replaced at build time by esbuild's `define` option.
// For local development it stays as-is if left unchanged in build.js.
// ---------------------------------------------------------------------------

const API_BASE_URL = "https://satlasapi.devalyze.com";

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

async function apiCheckAuthStatus() {
  const response = await fetch(`${API_BASE_URL}/account/status`, { credentials: 'include' });
  return response.json();
}

async function apiLoadProfile() {
  const response = await fetch(`${API_BASE_URL}/account/profile`, { credentials: 'include' });
  if (!response.ok) throw new Error('Profile load failed');
  return response.json();
}

async function apiLogin(email, password) {
  const response = await fetch(`${API_BASE_URL}/account/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username: email, password })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Login failed');
  return data;
}

async function apiRegister(email, displayName, fullName, password) {
  const response = await fetch(`${API_BASE_URL}/account/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username: email, displayName, fullName, password })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Registration failed');
  return data;
}

async function apiActivate(email, activationCode, password) {
  const response = await fetch(`${API_BASE_URL}/account/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, activationCode, password })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Activation failed');
  return data;
}

async function apiLogout() {
  const response = await fetch(`${API_BASE_URL}/account/logout`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Logout failed');
  return true;
}

async function apiSavePreferences(verifiedOnly, minScore, defaultSortingMethod) {
  const response = await fetch(`${API_BASE_URL}/account/preferences`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ verifiedOnly, minScore, defaultSortingMethod })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to save preferences');
  return data;
}

// ---------------------------------------------------------------------------
// Feed
// ---------------------------------------------------------------------------

async function apiFetchFeed(params) {
  let response;

  // The given params are expected to be a URLSearchParams object.
  // If this contains an "url" parameter, we fetch the feed for that specific URL; 
  // otherwise, we fetch the general feed.
  if(params.has('url')) {
    response = await fetch(`${API_BASE_URL}/feed/page?${params.toString()}`, { credentials: 'include' });
  } else {
    response = await fetch(`${API_BASE_URL}/feed?${params.toString()}`, { credentials: 'include' });
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(`${err.message || 'Failed to load feed'} (HTTP ${response.status})`);
  }
  return response.json();
}

async function apiFetchMessage(messageId) {
  const response = await fetch(`${API_BASE_URL}/feed/message/${encodeURIComponent(messageId)}`, { credentials: 'include' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Message not found');
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

async function apiSearch(query) {
  const response = await fetch(`${API_BASE_URL}/search?q=${encodeURIComponent(query)}`, {
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Search failed');
  return response.json();
}

// ---------------------------------------------------------------------------
// Voting & reporting
// ---------------------------------------------------------------------------

async function apiUpvote(messageId) {
  const response = await fetch(`${API_BASE_URL}/feed/upvote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ messageId })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Upvote failed');
  return data;
}

async function apiDownvote(messageId) {
  const response = await fetch(`${API_BASE_URL}/feed/downvote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ messageId })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Downvote failed');
  return data;
}

async function apiReport(messageId, reason) {
  const response = await fetch(`${API_BASE_URL}/feed/report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ messageId, reason })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Report failed');
  return data;
}

// ---------------------------------------------------------------------------
// Follow / unfollow
// ---------------------------------------------------------------------------

async function apiFollow(pubkey) {
  const response = await fetch(`${API_BASE_URL}/account/follow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ pubkey })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Follow failed');
  return data;
}

async function apiUnfollow(pubkey) {
  const response = await fetch(`${API_BASE_URL}/account/unfollow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ pubkey })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Unfollow failed');
  return data;
}

// ---------------------------------------------------------------------------
// Public-key account linking (3-step: init → sign → verify)
// ---------------------------------------------------------------------------

async function apiLinkPubkeyInit(pubkey) {
  const response = await fetch(`${API_BASE_URL}/account/link-pubkey-init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ pubkey })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Link init failed');
  return data;
}

async function apiLinkPubkeyVerify(signature) {
  const response = await fetch(`${API_BASE_URL}/account/link-pubkey-verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ signature })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Link verify failed');
  return data;
}

// ---------------------------------------------------------------------------
// Pubkey-based registration / login (used when linking a delegate to Satlas)
// ---------------------------------------------------------------------------

async function apiRegisterWithPubkey(pubkey, displayName, fullName) {
  const liteClient = window.liteClient;

  const initResponse = await fetch(`${API_BASE_URL}/account/register-pubkey-init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ pubkey, displayName, fullName })
  });
  const initData = await initResponse.json();
  if (!initResponse.ok) throw new Error(initData.message);

  const messageToSign = `Please sign this message to verify ownership: ${initData.nonce}`;
  const signature = await liteClient.signMessage(messageToSign);

  const verifyResponse = await fetch(`${API_BASE_URL}/account/register-pubkey-verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ signature })
  });
  const result = await verifyResponse.json();
  if (!verifyResponse.ok) throw new Error(result.message);
  return result;
}

async function apiLoginWithPubkey(pubkey) {
  const liteClient = window.liteClient;

  const initResponse = await fetch(`${API_BASE_URL}/account/login-pubkey-init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ pubkey })
  });
  const initData = await initResponse.json();
  if (!initResponse.ok) throw new Error(initData.message);

  const messageToSign = `Please sign this message to verify ownership: ${initData.nonce}`;
  const signature = await liteClient.signMessage(messageToSign);

  const verifyResponse = await fetch(`${API_BASE_URL}/account/login-pubkey-verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ signature })
  });
  const result = await verifyResponse.json();
  if (!verifyResponse.ok) throw new Error(result.message);
  return result;
}

async function apiCheckPubkeyExists(pubkey) {
  const response = await fetch(`${API_BASE_URL}/account/check-pubkey`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pubkey })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message);
  return data;
}

// ---------------------------------------------------------------------------
// WebSocket client for real-time updates
// ---------------------------------------------------------------------------
class WebSocketClient {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this._handlers = {};
  }

  on(event, handler) {
    if (!this._handlers[event]) this._handlers[event] = [];
    this._handlers[event].push(handler);
  }

  _emit(event, data) {
    const handlers = this._handlers[event];
    if (handlers) handlers.forEach(h => h(data));
  }

  async reconnect() {
    // While reconnect attempts is less than 5, try to reconnect with exponential backoff
    if (this.reconnectAttempts < 5) {
      const delay = Math.pow(2, this.reconnectAttempts) * 1000; // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      //console.log(`WebSocket connection lost. Attempting to reconnect in ${delay / 1000} seconds...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      this.reconnectAttempts++;
      await this.connect();
    } else {
      console.log('Max WebSocket reconnection attempts reached. Please refresh the page to try again.');
    }
  }

  async connect() {
    this.ws = new WebSocket(`${API_BASE_URL.replace(/^http/, 'ws')}/ws`);
    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
    };
    this.ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      //console.log('WebSocket message received:', data);
      if (data.type) this._emit(data.type, data.message ?? data);
    };
    this.ws.onclose = () => {
      //console.log('WebSocket disconnected');
      this.reconnect();
    };
    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    // Set up a keep-alive ping every 30 seconds
    setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }
}





















/**
 * shared/js/account.js
 *
 * Cryptographic account management — identical across all three platforms.
 *
 * Depends on:
 *   - shared/js/utils.js  (enc, dec, toHex, fromHex, base64urlDecodeToUint8)
 *   - window.liteClient   — LiteClient instance created by each app entry point
 *   - window.hashwasm     — hash-wasm library loaded via <script> tag
 */

//import { enc, toHex, fromHex, base64urlDecodeToUint8 } from './utils.js';

// ---------------------------------------------------------------------------
// IndexedDB constants
// ---------------------------------------------------------------------------

const DB_NAME           = 'account_store_db_v1';
const STORE_NAME        = 'accountstore';
const WRAPPED_RECORD_KEY = 'wrapped_seed_record';
const WEBAUTHN_CRED_KEY  = 'webauthn_cred_v1';

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

async function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (ev) => {
      const db = ev.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function idbPut(obj) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(obj);
    tx.oncomplete = () => resolve(true);
    tx.onerror    = (e) => reject(e.target.error);
  });
}

async function idbGet(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const r  = tx.objectStore(STORE_NAME).get(id);
    r.onsuccess = () => resolve(r.result);
    r.onerror   = (e) => reject(e.target.error);
  });
}

async function idbDelete(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve(true);
    tx.onerror    = (e) => reject(e.target.error);
  });
}

// ---------------------------------------------------------------------------
// PRF (Platform Resident Key / WebAuthn passwordless) helpers
// ---------------------------------------------------------------------------

async function supportsPRF() {
  if (!window.PublicKeyCredential) return false;
  try {
    if (typeof PublicKeyCredential.getClientCapabilities === 'function') {
      const caps = await PublicKeyCredential.getClientCapabilities('public-key');
      return (!!caps.extensions?.includes('prf') || !!caps['extension:prf'] === true);
    }
  } catch { /* feature not available */ }
  return false;
}

async function deriveAesKeyFromPrf(prfBytes, info = 'accountstore-prf') {
  const baseKey = await crypto.subtle.importKey('raw', prfBytes, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: enc(info) },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function getPrfForCredential(credIdHex, saltBytes) {
  const rawId = fromHex(credIdHex);
  const publicKey = {
    challenge:         crypto.getRandomValues(new Uint8Array(32)),
    allowCredentials:  [{ id: rawId, type: 'public-key' }],
    userVerification:  'required',
    timeout:           60000,
    extensions:        { prf: { eval: { first: saltBytes } } }
  };
  const assertion = await navigator.credentials.get({ publicKey });
  const ext       = assertion.getClientExtensionResults?.();
  const prfOut    = ext?.prf?.results?.first;
  if (!prfOut) throw new Error('PRF extension not returned');
  if (prfOut instanceof ArrayBuffer) return new Uint8Array(prfOut);
  if (typeof prfOut === 'string')    return base64urlDecodeToUint8(prfOut);
  if (prfOut instanceof Uint8Array)  return prfOut;
  if (prfOut.value instanceof ArrayBuffer) return new Uint8Array(prfOut.value);
  throw new Error('Unexpected PRF output type');
}

// ---------------------------------------------------------------------------
// Password derivation (Argon2id with PBKDF2 fallback)
// ---------------------------------------------------------------------------

async function deriveAesKey_PBKDF2(passphrase, saltHex, iterations = 200_000) {
  const salt    = fromHex(saltHex);
  const baseKey = await crypto.subtle.importKey('raw', enc(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function deriveAesKeyFromPassword(passphrase, saltHex) {
  try {
    const saltBytes = fromHex(saltHex);
    // window.hashwasm is loaded via <script src="hash-wasm/argon2.js">
    const argon2id  = window.hashwasm.argon2id;
    const hash = Uint8Array.fromHex(
      await argon2id({ password: passphrase, salt: saltBytes, iterations: 3, memorySize: 65536, parallelism: 1, hashLength: 32 })
    );
    return { type: 'argon2', aesKey: await crypto.subtle.importKey('raw', hash, 'AES-GCM', false, ['encrypt', 'decrypt']) };
  } catch (e) {
    console.warn('Argon2 failed, falling back to PBKDF2', e);
    return { type: 'pbkdf2', aesKey: await deriveAesKey_PBKDF2(passphrase, saltHex) };
  }
}

// ---------------------------------------------------------------------------
// AES-GCM helpers
// ---------------------------------------------------------------------------

async function aesGcmEncryptBytes(aesKey, plainBytes) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plainBytes);
  return { ivHex: toHex(iv), cipherHex: toHex(new Uint8Array(ct)) };
}

async function aesGcmDecryptBytes(aesKey, ivHex, cipherHex) {
  const iv    = fromHex(ivHex);
  const ct    = fromHex(cipherHex);
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ct);
  return new Uint8Array(plain);
}

// ---------------------------------------------------------------------------
// WebAuthn credential creation
// ---------------------------------------------------------------------------

async function createWebAuthnCredentialIfNeeded(enablePrf = false) {
  const rec = await idbGet(WEBAUTHN_CRED_KEY);
  if (rec && rec.rawIdHex) return rec.rawIdHex;

  const publicKey = {
    challenge:              crypto.getRandomValues(new Uint8Array(32)),
    rp:                     { name: 'ChatApp (local)' },
    user:                   { id: crypto.getRandomValues(new Uint8Array(16)), name: 'local-user', displayName: 'Local device' },
    pubKeyCredParams:       [{ alg: -7, type: 'public-key' }],
    authenticatorSelection: { userVerification: 'required' },
    timeout:                60000,
    attestation:            'none',
    extensions:             enablePrf ? { prf: {} } : undefined
  };

  const cred     = await navigator.credentials.create({ publicKey });
  const rawIdHex = toHex(new Uint8Array(cred.rawId));
  await idbPut({ id: WEBAUTHN_CRED_KEY, rawIdHex, createdAt: new Date().toISOString() });
  return rawIdHex;
}

// ---------------------------------------------------------------------------
// Core account functions
// ---------------------------------------------------------------------------

/**
 * Create or restore a local account.
 *
 * accountType: 'auto' | 'prf' | 'wan' | 'password'
 *   auto  → tries PRF first, then falls back to password
 *   prf   → passwordless hardware-key only
 *   wan   → password + hardware-key
 *   password → password only, no hardware key
 *
 * restoreMnemonic: supply an existing BIP-39 mnemonic to restore; pass false to create fresh.
 */
async function createAccount(password, accountType = 'auto', restoreMnemonic = false) {
  // window.liteClient is set by the app entry point before calling this
  const liteClient = window.liteClient;

  let mnemonic, raw, seedBytes;

  if (restoreMnemonic) {
    const accountData = await liteClient.loadAccount(restoreMnemonic);
    if (!accountData) throw new Error('Failed to restore account with provided mnemonic');
    mnemonic = accountData.mnemonic;
    raw      = accountData.seed;
    //mnemonic  = liteClient.account.mnemonic;
    //const raw = liteClient.account.seed;

    seedBytes = raw instanceof Uint8Array ? raw : new Uint8Array(raw);
  } else {
    await liteClient.createAccount();
    mnemonic  = liteClient.account.mnemonic;
    seedBytes = liteClient.account.seed instanceof Uint8Array
      ? liteClient.account.seed
      : new Uint8Array(liteClient.account.seed);
  }

  let record;

  if (await supportsPRF() && (accountType === 'auto' || accountType === 'prf')) {
    // Passwordless PRF-based wrap
    const credIdHex             = await createWebAuthnCredentialIfNeeded(true);
    const salt                  = crypto.getRandomValues(new Uint8Array(32));
    const prf                   = await getPrfForCredential(credIdHex, salt);
    const aesKey                = await deriveAesKeyFromPrf(prf);
    const { ivHex, cipherHex }  = await aesGcmEncryptBytes(aesKey, seedBytes);
    record = {
      id: WRAPPED_RECORD_KEY,
      method: 'prf',
      saltHex: toHex(salt),
      ivHex,
      cipherHex,
      createdAt: new Date().toISOString(),
      credIdHex
    };
  } else {
    // Password-based wrap
    if (!password) throw new Error('Password required');
    const salt                  = crypto.getRandomValues(new Uint8Array(16));
    const saltHex               = toHex(salt);
    const { type, aesKey }      = await deriveAesKeyFromPassword(password, saltHex);
    const { ivHex, cipherHex }  = await aesGcmEncryptBytes(aesKey, seedBytes);
    const webAuthnId = (accountType === 'auto' || accountType === 'wan')
      ? await createWebAuthnCredentialIfNeeded(false).catch(() => null)
      : null;
    record = {
      id: WRAPPED_RECORD_KEY,
      method: type,
      saltHex,
      ivHex,
      cipherHex,
      createdAt: new Date().toISOString(),
      webAuthnId
    };
  }

  await idbPut(record);
  await liteClient.loadAccount(seedBytes, 'seed');

  return { mnemonic, seedHex: toHex(seedBytes), derived: liteClient.account, stored: true, recordMeta: record };
}

/**
 * Decrypt and load a stored account seed.
 * Returns { seedHex, derived } on success, or false on failure.
 */
async function unlockAccount(password) {
  const liteClient = window.liteClient;
  const rec        = await idbGet(WRAPPED_RECORD_KEY);
  if (!rec) throw new Error('No stored account');

  try {
    let seedBytes;

    if (rec.method === 'prf') {
      const prf    = await getPrfForCredential(rec.credIdHex, fromHex(rec.saltHex));
      const aesKey = await deriveAesKeyFromPrf(prf);
      seedBytes    = await aesGcmDecryptBytes(aesKey, rec.ivHex, rec.cipherHex);
    } else {
      if (!password) throw new Error('Password required for this record');
      const { aesKey } = await deriveAesKeyFromPassword(password, rec.saltHex);
      seedBytes        = await aesGcmDecryptBytes(aesKey, rec.ivHex, rec.cipherHex);
    }

    await liteClient.loadAccount(seedBytes, 'seed');
    return { seedHex: toHex(seedBytes), derived: liteClient.account };
  } catch {
    return false;
  }
}

/**
 * Check whether a wrapped seed record exists in IndexedDB.
 * Returns { hasAccount: boolean, type: string|null }
 */
async function hasStoredAccount() {
  try {
    const rec = await idbGet(WRAPPED_RECORD_KEY);
    if (!rec) return { hasAccount: false, type: null };
    return { hasAccount: true, type: rec.method };
  } catch {
    return { hasAccount: false, type: null };
  }
}

/**
 * Delete the stored account and WebAuthn credential from IndexedDB.
 */
async function removeStoredAccount() {
  await idbDelete(WRAPPED_RECORD_KEY);
  await idbDelete(WEBAUTHN_CRED_KEY);
  return true;
}

/**
 * Restore an account from a BIP-39 mnemonic phrase.
 * Thin wrapper around createAccount.
 */
async function restoreAccount(mnemonic, type = 'auto', password = null) {
  if (!mnemonic) throw new Error('Mnemonic required');
  return createAccount(password, type, mnemonic);
}

/***
 * Get the current reputation of the local account
 * Returns { reputation: number } on success, or false on failure.
 */
function getAccountReputation() {
  const liteClient = window.liteClient;
  if (!liteClient?.user) throw new Error('Account not loaded');
  try {
    return { reputation: liteClient.user.reputation };
  } catch {
    return false;
  }
}

/***
 * Delete all local data including the account and all messages stored in IndexedDB.
 */
async function deleteAllLocalData() {
  await removeStoredAccount();
  const liteClient = window.liteClient;
  if (liteClient) {
    await liteClient.database.clear();
  }
  return true;
}




























/**
 * shared/js/feed.js
 *
 * REST-API-based feed rendering helpers shared by the web app and the
 * Chrome Extension.  Both apps query the same Satlas backend.
 *
 * The Electron app uses a purely P2P chat flow and does NOT use this module.
 *
 * Depends on:
 *   - shared/js/utils.js  (escapeHtml)
 */

function decodeHtmlEntities(text) {
  let value = String(text ?? '');
  if (!/&(?:#\d+|#[xX][\da-fA-F]+|[a-zA-Z][\w]+);/.test(value)) return value;

  const textarea = document.createElement('textarea');
  for (let pass = 0; pass < 3; pass += 1) {
    textarea.innerHTML = value;
    const decoded = textarea.value;
    if (decoded === value) break;
    value = decoded;
  }
  return value;
}

function escapeMetadataText(value) {
  return escapeHtml(decodeHtmlEntities(value));
}

function splitAuthorName(name) {
  const value = String(name || 'Unknown').trim();
  const suffixIndex = value.indexOf('#');
  if (suffixIndex < 0) return { primary: value, suffix: '' };
  return {
    primary: value.slice(0, suffixIndex).trim(),
    suffix: value.slice(suffixIndex).trim()
  };
}

function getPrimaryVerifiedDomain(profile = {}) {
  const primaryDomain = typeof profile.primaryDomain === 'string'
    ? profile.primaryDomain
    : profile.primaryDomain?.domain;
  if (String(primaryDomain || '').trim()) return String(primaryDomain).trim();
  const domains = Array.isArray(profile.verifiedDomains) ? profile.verifiedDomains : [];
  const primary = domains.find(domain => domain?.primary || domain?.isPrimary) || domains[0];
  return String(typeof primary === 'string' ? primary : primary?.domain || '').trim();
}

function getAvatarUrl(profile = {}) {
  const source = profile.avatarUrl ?? profile.avatar ?? profile.profilePicture ?? '';
  const raw = typeof source === 'string'
    ? source
    : String(source?.url || source?.src || source?.image || '').trim();
  if (!raw) return '';
  if (/^(https?:|data:image|blob:)/i.test(raw)) return raw;
  try { return new URL(raw, API_BASE_URL).href; } catch { return ''; }
}

function getPublicUsername(profile = {}) {
  const candidates = [
    profile?.userName,
    profile?.username,
    profile?.user?.userName,
    profile?.user?.username
  ];
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (!value || ['null', '<null>', 'nil', 'undefined', '(null)'].includes(value.toLowerCase())) continue;
    return value.replace(/^@+/, '');
  }
  return '';
}

function getProfileIdentity(profile = {}) {
  const userName = getPublicUsername(profile);
  const displayName = String(profile.displayName || '').trim();
  const fullName = String(profile.fullName || '').trim();
  const displayParts = splitAuthorName(displayName || fullName || 'Unknown');
  const fullParts = splitAuthorName(fullName || displayName || 'Unknown');
  const fallbackPrimary = fullParts.primary || displayParts.primary || 'Unknown';
  const assignedCode = displayParts.suffix || fullParts.suffix;
  const primaryDomain = getPrimaryVerifiedDomain(profile);
  const secondary = userName
    ? (primaryDomain || displayName || fullName)
    : (primaryDomain || assignedCode);
  return {
    primary: userName || fallbackPrimary,
    secondary: secondary && secondary !== (userName || fallbackPrimary) ? secondary : '',
    verified: Boolean(userName),
    initial: (userName || fallbackPrimary).charAt(0).toUpperCase(),
    avatarUrl: getAvatarUrl(profile),
    primaryDomain
  };
}

function getProfileUrlSlug(profile = {}) {
  return getPublicUsername(profile);
}

function getAssignedProfileUrlSlug(profile = {}) {
  const identity = getProfileIdentity(profile);
  const fullName = String(profile.fullName || '').trim();
  const displayName = String(profile.displayName || '').trim();
  const assignedName = [fullName, displayName].find(value => value.includes('#')) || '';
  const source = assignedName || identity.primary;
  return source
    .normalize('NFKC')
    .replace(/#/g, '-')
    .replace(/[^\p{L}\p{N}._~-]+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function getProfilePath(profile = {}, pubkey = '') {
  const slug = getProfileUrlSlug(profile);
  return `/u/${encodeURIComponent(slug || pubkey || getPersonPubkey(profile))}`;
}

function getProfileSearchQuery(identifier) {
  const value = String(identifier || '').trim();
  return value.replace(/-([\p{L}\p{N}_-]{7})$/u, '#$1');
}

function getPersonPubkey(person = {}) {
  return String(person.pubkey || person.publicKey || person.delegatePubkey || '').trim();
}

function getMessageRating(message = {}) {
  if (typeof message.rating !== 'number' && typeof message.rating !== 'string') return 0;
  const rating = Number(message.rating);
  return Number.isFinite(rating) && rating > 0 ? rating : 0;
}

function formatMessageTimestamp(value, now = Date.now()) {
  const date = new Date(value);
  const timestamp = date.getTime();
  if (!Number.isFinite(timestamp)) return '';

  const elapsedHours = Math.max(1, Math.floor(Math.max(0, now - timestamp) / 3600000));
  if (elapsedHours <= 24) return `${elapsedHours}h`;

  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(date);
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}


function buildMessageShareUrl(message = {}) {
  const messageId = String(message.messageId || '').trim();
  return messageId ? `${window.location.origin}/m/${encodeURIComponent(messageId)}` : '';
}

function createShareControlHtml(shareUrl, shareTitle) {
  if (!shareUrl) return '';
  return `
    <div class="share-control">
      <button class="action-btn share-trigger" type="button" data-action="open-share-popup" data-share-url="${escapeHtml(shareUrl)}" data-share-title="${escapeHtml(shareTitle)}" aria-label="Share post" aria-haspopup="dialog">
        <i class="fa-light fa-arrow-up-from-bracket" aria-hidden="true"></i>
      </button>
    </div>`;
}

function embeddedUrlPreviewHtml(message, link, pageUrl) {
  if (!link?.href) return '';
  const embeddedMetadata = findEmbeddedUrlPreview(link.href, message.inMessageUrls)
    || findEmbeddedUrlPreview(link.href, message.embeddedUrlPreviews);
  const canReuseAttachedMetadata = pageUrl && urlsReferenceSamePage(link.href, pageUrl);
  const embeddedCardValue = embeddedMetadata?.socialCard
    || embeddedMetadata?.metadata
    || embeddedMetadata?.preview
    || embeddedMetadata;
  const embeddedCard = embeddedCardValue && typeof embeddedCardValue === 'object'
    ? embeddedCardValue
    : null;
  const attachedCard = canReuseAttachedMetadata && message.socialCard && typeof message.socialCard === 'object'
    ? message.socialCard
    : null;
  const socialCard = (embeddedCard?.title || embeddedCard?.image) ? embeddedCard : attachedCard;
  const urlInfo = embeddedMetadata?.urlInfo
    || (canReuseAttachedMetadata ? message.urlInfo : null)
    || {};
  const displayUrl = compactDisplayUrl(link.href);
  let domain = displayUrl.split('/')[0];
  try { domain = new URL(link.href).hostname.replace(/^www\./i, ''); } catch { /* Use the compact label. */ }
  const sourceText = String(socialCard?.siteName || domain || displayUrl).trim();
  const verifiedBadge = urlInfo.verified === true
    ? '<i class="fa-solid fa-badge-check source-domain-verified" aria-label="Verified website"></i>'
    : '';
  const labelText = socialCard?.siteName ? escapeMetadataText(sourceText) : escapeHtml(sourceText);
  const label = `<span class="source-address-title">${labelText}</span>${verifiedBadge}`;
  const href = `/p/${encodeURIComponent(link.href)}`;
  const data = `data-page-url="${escapeHtml(link.href)}"`;

  if (!socialCard?.title && !socialCard?.image) {
    return `<a href="${href}" class="source-address embedded-page-preview" ${data}><span class="source-address-main"><span class="source-address-title">${escapeHtml(displayUrl)}</span>${verifiedBadge}</span></a>`;
  }

  return `<a href="${href}" class="social-card embedded-page-preview" ${data}>
    <div class="social-card-address"><span class="source-address-main">${label}</span></div>
    <div class="social-card-body">
      ${socialCard.image ? `<div class="social-card-image" style="background-image:url('${escapeHtml(socialCard.image)}')"></div>` : ''}
      <div class="social-card-content">
        ${socialCard.title ? `<div class="social-card-title">${escapeMetadataText(socialCard.title)}</div>` : ''}
        ${socialCard.description ? `<div class="social-card-description">${escapeMetadataText(socialCard.description)}</div>` : ''}
        <div class="social-card-url">${escapeHtml(displayUrl)}</div>
      </div>
    </div>
  </a>`;
}

// ---------------------------------------------------------------------------
// Message card rendering
// ---------------------------------------------------------------------------

/**
 * Build the HTML string for a single message card.
 *
 * config options:
 *   isLoggedIn   {boolean}  — show vote/reply/report buttons (true) or plain counts (false)
 *   currentUser  {object}   — logged-in user object; used to highlight own votes
 *   feedType     {string}   — 'global' | 'page'; "View Source" fallback only shown for global
 *   primaryColor {string}   — hex colour for the verification badge icon
 *   inThread     {boolean}  — omit channel label and social card (true when rendering a reply)
 *   promoteFirstEmbeddedUrl {boolean} — move the first text URL into a Page preview beneath the remaining text
 */
function createMessageCard(message, config = {}) {
  const {
    isLoggedIn = false,
    currentUser = null,
    feedType = 'global',
    inThread = false,
    webPageView = false,
    promoteFirstEmbeddedUrl = false,
    authorProfile = null
  } = config;

  const identity = getProfileIdentity({ ...message, ...(authorProfile || {}) });
  const authorProfilePath = getProfilePath({ ...message, ...(authorProfile || {}) }, message.pubkey);
  const authorPrimary = identity.primary;
  const pubkey = String(message.pubkey || '');
  const messageId = String(message.messageId || '');
  const pageUrl = message.canonicalUrl || message.url || '';
  const timestamp = message.timestamp ? formatMessageTimestamp(message.timestamp) : '';
  const channel = message.urlInfo?.domain || (() => {
    try { return new URL(pageUrl).hostname.replace(/^www\./, ''); } catch { return ''; }
  })();
  const sourceTitleText = String(message.socialCard?.siteName || channel || '').trim();
  const sourceTitle = sourceTitleText
    ? `<span class="source-address-title">${escapeMetadataText(sourceTitleText)}</span>`
    : '';
  const sourceDomainVerification = sourceTitle && message.urlInfo?.verified === true
    ? '<i class="fa-solid fa-badge-check source-domain-verified" aria-label="Verified website"></i>'
    : '';
  const verifiedSourceLabel = `${sourceTitle}${sourceDomainVerification}`;
  const messageRating = getMessageRating(message);
  const sourceRating = messageRating > 0
    ? `<span class="source-rating" aria-label="Rating ${messageRating}"><span class="source-rating-kekko-icon" aria-hidden="true"></span><span class="source-rating-value">${messageRating}</span></span>`
    : '';
  const pagePostCount = Number.isInteger(Number(message._pagePostCount)) && Number(message._pagePostCount) > 0
    ? Number(message._pagePostCount)
    : 0;
  const sourcePostCount = `<span class="source-post-count${pagePostCount > 0 ? '' : ' hidden'}" aria-label="Posts on this page"><i class="fa-light fa-message" aria-hidden="true"></i><span>${pagePostCount > 0 ? pagePostCount : ''}</span></span>`;
  const sourceMetadata = sourceRating || sourcePostCount
    ? `<span class="source-address-meta">${sourceRating}${sourcePostCount}</span>`
    : '';
  const verificationBadge = identity.verified
    ? '<i class="fa-solid fa-badge-check verified-account-badge" aria-label="Verified username"></i>'
    : '';
  const messageRatingBadge = (feedType === 'page' || inThread) && messageRating > 0
    ? `<div class="message-rating-badge" aria-label="Rating ${escapeHtml(messageRating)}"><span class="message-rating-kekko-icon" aria-hidden="true"></span><span>${escapeHtml(messageRating)}</span></div>`
    : '';
  const upvoteCount = Number(message.upvoteCount) || 0;
  const downvoteCount = Number(message.downvoteCount) || 0;
  const replyCount = Number(message.replyCount) || 0;
  const canReply = (message.depth ?? 0) < 2;
  const hasUpvoted = currentUser && message.upvotes?.includes(currentUser.userId);
  const hasDownvoted = currentUser && message.downvotes?.includes(currentUser.userId);
  const taggedUsers = Array.isArray(message.taggedUsers) && message.taggedUsers.length
    ? message.taggedUsers
    : (Array.isArray(message.taggedUserInfo) ? message.taggedUserInfo : []);
  const shareControl = createShareControlHtml(buildMessageShareUrl(message), `Post by ${authorPrimary}`);
  const messageOptions = isLoggedIn
    ? `<div class="message-options">
         <button class="message-options-trigger" type="button" data-action="toggle-options" aria-label="Post options" aria-haspopup="menu" aria-expanded="false"><i class="fa-light fa-ellipsis" aria-hidden="true"></i></button>
         <div class="message-options-menu hidden" role="menu"><button class="message-options-option" type="button" role="menuitem" data-action="report" data-message-id="${escapeHtml(messageId)}">Report</button></div>
       </div>`
    : '';

  const urlPresentation = prepareMessageUrlPresentation(message.message || 'No content', {
    promoteFirstUrl: promoteFirstEmbeddedUrl
  });
  let content = escapeHtml(urlPresentation.text);
  for (const taggedUser of taggedUsers) {
    const taggedPubkey = String(taggedUser?.pubkey || taggedUser?.delegatePubkey || taggedUser?.userPubkey || '').trim();
    if (!taggedPubkey) continue;
    const publicUsername = getPublicUsername(taggedUser);
    const mention = String(publicUsername || taggedUser?.displayName || taggedUser?.fullName || taggedPubkey).trim();
    const identifiers = [...new Set([
      taggedPubkey,
      publicUsername,
      taggedUser?.displayName,
      taggedUser?.fullName
    ].map(value => String(value || '').trim().replace(/^@+/, '')).filter(Boolean))];
    const link = `<a href="${getProfilePath(taggedUser, taggedPubkey)}" class="tagged-user" data-pubkey="${escapeHtml(taggedPubkey)}">@${escapeHtml(mention)}</a>`;
    for (const identifier of identifiers) {
      const regex = new RegExp(`@${escapeRegExp(identifier)}(?=$|[\\s.,!?;:()\\[\\]{}])`, 'g');
      content = content.replace(regex, link);
    }
  }
  for (const { token, href, display } of urlPresentation.links) {
    const pageHref = `/p/${encodeURIComponent(href)}`;
    const link = `<a href="${pageHref}" class="message-inline-url" data-page-url="${escapeHtml(href)}">${escapeHtml(display)}</a>`;
    content = content.replace(token, () => link);
  }

  const embeddedPreview = embeddedUrlPreviewHtml(message, urlPresentation.promoted, pageUrl);

  let socialCard = '';
  if (!inThread && feedType !== 'page' && pageUrl) {
    const href = webPageView ? `/p/${encodeURIComponent(pageUrl)}` : escapeHtml(pageUrl);
    const target = webPageView ? '' : ' target="_blank" rel="noopener noreferrer"';
    if (message.socialCard?.title || message.socialCard?.image) {
      socialCard = `<a href="${href}" class="social-card"${target}>
        ${verifiedSourceLabel || sourceMetadata ? `<div class="social-card-address"><span class="source-address-main">${verifiedSourceLabel}</span>${sourceMetadata}</div>` : ''}
        <div class="social-card-body">
          ${message.socialCard.image ? `<div class="social-card-image" style="background-image:url('${escapeHtml(message.socialCard.image)}')"></div>` : ''}
          <div class="social-card-content">
            ${message.socialCard.title ? `<div class="social-card-title">${escapeMetadataText(message.socialCard.title)}</div>` : ''}
            ${message.socialCard.description ? `<div class="social-card-description">${escapeMetadataText(message.socialCard.description)}</div>` : ''}
            <div class="social-card-url">${escapeHtml(compactDisplayUrl(pageUrl))}</div>
          </div>
        </div>
      </a>`;
    } else if (verifiedSourceLabel) {
      socialCard = `<a href="${href}" class="source-address"${target}><span class="source-address-main">${verifiedSourceLabel}</span>${sourceMetadata}</a>`;
    }
  }

  const voteArea = isLoggedIn
    ? `<div class="message-actions">
         <button class="action-btn ${hasUpvoted ? 'active' : ''}" data-action="upvote" data-message-id="${escapeHtml(messageId)}" aria-label="Upvote"><i class="fa-light fa-arrow-up" aria-hidden="true"></i><span>${upvoteCount}</span></button>
         <button class="action-btn ${hasDownvoted ? 'active' : ''}" data-action="downvote" data-message-id="${escapeHtml(messageId)}" aria-label="Downvote"><i class="fa-light fa-arrow-down" aria-hidden="true"></i><span>${downvoteCount}</span></button>
         ${canReply ? `<button class="action-btn reply-count-btn" data-action="open-thread" data-message-id="${escapeHtml(messageId)}" aria-label="View replies"><i class="fa-light fa-reply" aria-hidden="true"></i><span>${replyCount}</span></button>` : ''}
         ${shareControl}
       </div>`
    : `<div class="message-actions">
         <button class="action-btn" type="button" disabled aria-label="Log in to upvote"><i class="fa-light fa-arrow-up" aria-hidden="true"></i><span>${upvoteCount}</span></button>
         <button class="action-btn" type="button" disabled aria-label="Log in to downvote"><i class="fa-light fa-arrow-down" aria-hidden="true"></i><span>${downvoteCount}</span></button>
         ${canReply ? `<button class="action-btn reply-count-btn" type="button" disabled aria-label="Log in to reply"><i class="fa-light fa-reply" aria-hidden="true"></i><span>${replyCount}</span></button>` : ''}
         ${shareControl}
       </div>`;

  return `<article class="message-card" data-message-id="${escapeHtml(messageId)}" data-pubkey="${escapeHtml(pubkey)}" data-page-url="${escapeHtml(pageUrl)}">
    <div class="message-header">
      <a class="message-author" href="${authorProfilePath}" aria-label="Open ${escapeHtml(authorPrimary)} profile">
        <div class="message-avatar" aria-hidden="true">${escapeHtml(identity.initial)}${identity.avatarUrl ? `<img src="${escapeHtml(identity.avatarUrl)}" alt="">` : ''}</div>
        <div class="message-info">
          <div class="message-name">${escapeHtml(authorPrimary)}${verificationBadge}</div>
          ${identity.secondary ? `<div class="message-username">${escapeHtml(identity.secondary)}</div>` : ''}
        </div>
      </a>
      <div class="message-meta-info">
        ${timestamp ? `<time class="message-time" datetime="${new Date(message.timestamp).toISOString()}">${timestamp}</time>` : ''}
        <div class="message-status-row"><div class="message-time">${scoreToString(message.score || 0)}</div>${messageOptions}</div>
      </div>
    </div>
    <div class="message-content">${urlPresentation.text.trim() ? `<div class="message-text">${content}</div>` : ''}${embeddedPreview}${messageRatingBadge}${socialCard}${voteArea}</div>
  </article>`;
}

// ---------------------------------------------------------------------------
// Channel list rendering
// ---------------------------------------------------------------------------

/**
 * Fetch channels from the backend and populate #channelsList.
 *
 * config:
 *   apiBaseUrl          {string}   — backend root URL
 *   currentChannel      {string}   — currently active channel (for active state)
 *   emptyStateClass     {string}   — CSS class for empty/error messages (default: 'empty-state')
 */
async function loadChannels(config = {}) {
  const {
    apiBaseUrl      = '',
    currentChannel  = null,
    emptyStateClass = 'empty-state'
  } = config;

  const channelsList = document.getElementById('channelsList');
  if (!channelsList) return;

  try {
    const response = await fetch(`${apiBaseUrl}/channels`, { credentials: 'include' });

    if (response.ok) {
      const data = await response.json();

      if (!data.length) {
        channelsList.innerHTML = `<p class="${emptyStateClass}">No channels available</p>`;
        return;
      }

      const channels = new Set(data.filter(Boolean));

      if (!channels.size) {
        channelsList.innerHTML = `<p class="${emptyStateClass}">No channels available</p>`;
        return;
      }

      channelsList.innerHTML = `
        <div class="channel-item ${!currentChannel ? 'active' : ''}" data-channel="all">
          <strong>All Channels</strong>
        </div>
        ${Array.from(channels).map(ch => `
          <div class="channel-item ${currentChannel === ch ? 'active' : ''}" data-channel="${escapeHtml(ch)}">
            ${escapeHtml(ch)}
          </div>
        `).join('')}`;
    } else {
      channelsList.innerHTML = `<p class="${emptyStateClass}">Failed to load channels</p>`;
    }
  } catch {
    channelsList.innerHTML = `<p class="${emptyStateClass}">Error loading channels</p>`;
  }
}

// ---------------------------------------------------------------------------
// Infinite scroll observer
// ---------------------------------------------------------------------------

/**
 * Set up an IntersectionObserver that triggers loadMoreFn when the loading
 * indicator element (identified by loadingIndicatorId) scrolls into view.
 *
 * The returned observer's observe() will be called automatically whenever
 * the loading indicator is added to the DOM via a MutationObserver on
 * #messagesContainer.
 *
 * @param {Function} loadMoreFn        — called when more messages are needed
 * @param {string}   loadingIndicatorId — id of the sentinel element
 * @returns {IntersectionObserver}
 */
function setupIntersectionObserver(loadMoreFn, loadingIndicatorId = 'loading-indicator') {
  const container = document.getElementById('messagesContainer');
  let observedElement = null;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target === observedElement
          && entry.target.isConnected && container?.contains(entry.target)
          && document.getElementById(loadingIndicatorId) === entry.target) loadMoreFn();
      });
    },
    { root: null, rootMargin: '100px', threshold: 0.1 }
  );

  const attach = () => {
    const el = document.getElementById(loadingIndicatorId);
    if (observedElement === el) return;
    if (observedElement) observer.unobserve(observedElement);
    observedElement = el;
    if (el && container?.contains(el)) observer.observe(el);
  };

  const mutationObserver = new MutationObserver(attach);
  if (container) {
    mutationObserver.observe(container, { childList: true });
    attach();
  }

  return observer;
}















/**
 * shared/js/utils.js
 *
 * Pure utility functions with no external dependencies.
 * Used identically by all three platforms (web, Electron, Chrome Extension).
 */

// ---------------------------------------------------------------------------
// Encoding helpers
// ---------------------------------------------------------------------------

const enc = s => new TextEncoder().encode(s);
const dec = b => new TextDecoder().decode(b);
const toHex = (u8) => Array.from(u8).map(x => x.toString(16).padStart(2, '0')).join('');
const fromHex = (hex) => new Uint8Array((hex.match(/.{1,2}/g) || []).map(b => parseInt(b, 16)));

function base64urlEncode(u8) {
  const s = btoa(String.fromCharCode(...u8));
  return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlDecodeToUint8(s) {
  s = s.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  const bin = atob(s);
  return new Uint8Array(Array.from(bin).map(c => c.charCodeAt(0)));
}

// ---------------------------------------------------------------------------
// XSS prevention
// ---------------------------------------------------------------------------

function escapeHtml(text) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return String(text).replace(/[&<>"']/g, m => map[m]);
}

// ---------------------------------------------------------------------------
// URL cleaning — strips tracking / affiliate / session parameters
// ---------------------------------------------------------------------------

const IGNORED_PARAMS = [
  // Google Analytics / Marketing
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'dclid', 'gclsrc', 'utm_id', 'utm_name', 'utm_reader',
  'utm_source_app',

  // Google Ads
  'gad_source', 'gad_campaignid', 'gbraid',
  'gad_adid', 'gad_adgroupid', 'gad_keywordid', 'gad_network', 'gad_device',
  'gad_placement',

  // Facebook
  'fbclid', 'fb_source', 'fb_action_ids', 'fb_action_types', 'fb_ref', 'fb_comment_id',

  // Microsoft / Bing / LinkedIn
  'msclkid', 'li_fat_id', 'li_giant',

  // Twitter / X
  'twclid',

  // TikTok
  'ttclid', 'tt_medium', 'tt_content',

  // Pinterest
  'epik',

  // Yahoo
  'yclid', 'guccounter',

  // Taboola
  'tbclid',

  // Outbrain
  'obOrigUrl',

  // Yandex Metrica
  'ymclid',

  // Baidu
  'bd_vid',

  // Adobe / Marketo
  'cid', 'icid', 'mkt_tok', 's_cid', 'cmpid', 'ef_id',

  // Salesforce / Eloqua / Pardot / HubSpot
  'elqTrackId', 'elqTrack', 'utm_email', 'utm_termid', 'hsa_acc', 'hsa_cam',
  'hsa_grp', 'hsa_ad', 'hsa_src', 'hsa_tgt', 'hsa_kw', 'hsa_net', 'hsa_ver',
  'hsCtaTracking', 'hsutk', 'hubspotutk',

  // Mailchimp
  'mc_cid', 'mc_eid',

  // Klaviyo
  '_kx',

  // ActiveCampaign / Vero
  'vgo_ee',

  // Drip
  '__s',

  // ConvertKit
  'ck_subscriber_id',

  // Iterable
  'iterableEmailCampaignId', 'iterableTemplateId', 'iterableMessageId',

  // DotDigital
  'dm_i', 'dm_t',

  // Sailthru
  'sailthru_mid', 'sailthru_bid',

  // Oracle / Responsys
  'riid', 'sc_campaign', 'sc_channel', 'sc_content', 'sc_medium', 'sc_outcome',

  // Branch.io (deep linking + attribution)
  '_branch_match_id', '_branch_referrer',

  // Wicked Reports / Hyros
  'wicked_source', 'wicked_medium',

  // Affiliate / Referral
  'ref', 'referrer', 'affiliate', 'affid', 'aff', 'src', 'partner', 'track',
  'tracking_id', 'campaignid', 'adgroupid', 'adid', 'adposition', 'creative',
  'network', 'device', 'feeditemid',

  // Session / Identity / Personalization
  'sessionid', 'phpsessid', 'jsessionid', 'sid', 'uid', 'userid',
  'visitor_id', 'user_token',

  // A/B Testing / Optimization
  'variant', 'split', 'experiments', 'expid', 'ab', 'testgroup', 'test',
  'optimizely_token', 'optimizely_x', 'optimizely_y', 'optimizely_z',

  // Miscellaneous
  'ncid', 'spm', 'WT.mc_id', 'WT.mc_ev', 'WT.srch', 'WT.mc_rid',
  'icn', 'iclid', 'sourceid',
  'sr_share', 'fbshare', 'share', 'campaign', 'ga_source',
  'ga_campaign', 'trk', 'trkCampaign', 'trkEmail', 'trkContact',

  // URL Shorteners or Custom Campaign Systems
  'shortlink', 'linkid', 'source', 'origreferrer',

  // Shopify / E-commerce
  'coupon', 'discount', 'promo', 'promocode',

  // Reddit
  'st', 'sh', 't', 's',

  // Google Search noise
  'rlz', 'gs_lcrp', 'sca_esv', 'ei', 'iflsig', 'ved', 'sxsrf', 'gs_ssp',
  'uact', 'oq', 'gs_lp', 'sclient', 'sei', 'bih', 'biw',

  // Amazon
  'mcid', 'linkCode', 'hvadid', 'hvpos', 'hvnetw', 'hvrand',
  'hvpone', 'hvptwo', 'hvqmt', 'hvdev', 'hvdvcmdl', 'hvlocint',
  'hvlocphy', 'hvtargid', 'tag', 'psc', '_encoding', 'content-id',
  'ref_', 'dib', 'dib_tag', 'qid', 'sr', 'keywords', 'node',
  'pd_rd_i', 'sprefix', 'crid', 'th', 'smid',
];

const IGNORED_PARAMS_SET = new Set(IGNORED_PARAMS.map(p => p.toLowerCase()));

// Prefix-based matching, catches entire families without enumerating every variant.
// e.g. any future gad_*, utm_*, hsa_* param is automatically stripped.
const IGNORED_PREFIXES = [
  'utm_',
  'hsa_',
  'gad_',
  'pd_rd_',
  'pf_rd_',
  'iterable',
  'wt.',
];

function isIgnoredParam(key) {
  const lower = key.toLowerCase();
  if (IGNORED_PARAMS_SET.has(lower)) return true;
  return IGNORED_PREFIXES.some(prefix => lower.startsWith(prefix));
}

// Normalize Amazon URLs to just the ASIN, dropping all query params and the
// /ref=... path segment. Falls through without wiping search if no ASIN found,
// so the normal param-stripping pass still runs as a fallback.
function cleanAmazonURL(url) {
  // Strip /ref=... path segment first (it sits before the ? in Amazon URLs)
  url.pathname = url.pathname.replace(/\/ref=[^/]*/g, '');
 
  const asinMatch = url.pathname.match(/\/(?:dp|gp\/product)\/([A-Z0-9]{10})/i);
  if (asinMatch) {
    url.pathname = `/dp/${asinMatch[1].toUpperCase()}`;
    url.search = '';
    url.hash = '';
  }
}

function cleanURL(urlString) {
  if (!urlString) return null;
 
  let url;
  try {
    url = new URL(urlString);
  } catch {
    return null;
  }
 
  const isAmazon = url.hostname.includes('amazon.');
 
  if (isAmazon) {
    cleanAmazonURL(url);
  }
 
  // Always run param stripping. On Amazon this is a no-op when an ASIN was found
  // (search is already empty), but acts as a fallback for Amazon URLs without one.
  for (const key of [...url.searchParams.keys()]) {
    if (isIgnoredParam(key)) {
      url.searchParams.delete(key);
    }
  }
 
  // Strip /ref=... path segments on non-Amazon sites too (some affiliate links use it)
  if (!isAmazon) {
    url.pathname = url.pathname.replace(/\/ref=[^/]*/g, '');
  }
 
  // Sort remaining params for a stable, canonical URL (useful for dedup / caching)
  const sortedParams = [...url.searchParams.entries()].sort(([a], [b]) =>
    a.localeCompare(b)
  );
  url.search = '';
  for (const [k, v] of sortedParams) {
    url.searchParams.append(k, v);
  }
 
  const search = url.searchParams.toString();

  // Return including search params and hash, but only if they exist (avoid trailing ? or #)
  return url.origin + url.pathname + (search ? '?' + search : '') + (url.hash ? url.hash : '');

  // Return excluding search params and hash for a cleaner, more canonical URL
  //return url.origin + url.pathname;
}

function scoreToString(score) {
  if (score === null || score === undefined) return 'N/A';

  if(score < -4) { // -10 to -4
    return 'Very Low';
  } else if(score < -2) { // -3 to -2
    return 'Low';
  } else if(score < 2) { // -1 to 1
    return 'Neutral';
  } else if(score < 4) { // 2 to 3
    return 'High';
  } else { // 4 to 10
    return 'Very High';
  }
}

function stringToScore(str) {
  switch(str) {
    case 'Very Low':
      return -10;
    case 'Low':
      return -3;
    case 'Neutral':
      return -1;
    case 'High':
      return 2;
    case 'Very High':
      return 4;
    default:
      return null;
  }
}




























// Initialize liteClient (LiteClient global is provided by satchat/dist.js script tag)
//const liteClient = new LiteClient();
//window.liteClient = liteClient;

// State Management
let currentUser = null;
let isLoggedIn = false;
let currentChannel = null;
let currentUrl = null;
let currentUserProfile = null;
let messages = [];
let currentPagePostCount = null;
let currentPageAverageRating = null;
let currentPageSummaryError = '';
const PAGE_POST_METADATA_TTL_MS = 60 * 1000;
const PAGE_POST_METADATA_BATCH_SIZE = 100;
const pagePostMetadataCache = new Map();
const pageMetadataJobs = [];
let activePageMetadataJobs = 0;
const MENTION_PROFILE_TTL_MS = 5 * 60 * 1000;
const mentionProfileCache = new Map();
let hasMore = true;
let isLoading = false;
let feedLoadGeneration = 0;
let activeRouteGeneration = 0;
let lastMessageTimestamp = null;
let intersectionObserver = null;
let userPreferences = {
  verifiedOnly: false,
  minScore: -10,
  defaultSortingMethod: 'new',
};
let searchTimeout = null;
let pendingReportMessageId = null;
let currentProfileViewState = null;
let currentProfileRelationshipState = null;
let searchPageState = {
  query: '',
  activeTab: 'page',
  loading: false,
  results: { pages: [], people: [], posts: [] }
};
let searchRequestId = 0;

const ROUTE_COVER_DURATION_MS = 200;
const ROUTE_REVEAL_DURATION_MS = 260;
const ROUTE_LOADER_DELAY_MS = 400;
const ROUTE_LOADER_MINIMUM_MS = 260;
const ROUTE_MEDIA_TIMEOUT_MS = 2500;
let routeTransitionSequence = 0;

function waitForRouteTransition(milliseconds) {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

function waitForRoutePaint() {
  return new Promise(resolve => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
}

async function animateRouteOverlay(overlay, targetOpacity, duration) {
  overlay.getAnimations().forEach(animation => animation.cancel());
  const currentOpacity = Number.parseFloat(window.getComputedStyle(overlay).opacity) || 0;
  if (duration <= 0 || Math.abs(currentOpacity - targetOpacity) < 0.001) {
    overlay.style.opacity = String(targetOpacity);
    return;
  }

  const animation = overlay.animate(
    [{ opacity: currentOpacity }, { opacity: targetOpacity }],
    {
      duration,
      easing: 'cubic-bezier(0.2, 0.75, 0.25, 1)',
      fill: 'forwards'
    }
  );
  try {
    await animation.finished;
  } catch {
    return;
  }
  overlay.style.opacity = String(targetOpacity);
  animation.cancel();
}

async function waitForVisibleRouteMedia() {
  await waitForRoutePaint();
  const images = [...document.images].filter(image => {
    if (image.closest('#routeTransition') || !image.getClientRects().length) return false;
    const rect = image.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight + 120;
  });
  if (!images.length) return;

  const ready = images.map(image => {
    if (image.complete) return image.decode?.().catch(() => {}) || Promise.resolve();
    return new Promise(resolve => {
      const finish = () => {
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        Promise.resolve(image.decode?.()).catch(() => {}).finally(resolve);
      };
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
    });
  });

  await Promise.race([
    Promise.all(ready),
    waitForRouteTransition(ROUTE_MEDIA_TIMEOUT_MS)
  ]);
}

async function runRouteTransition(renderRoute, { showLoader = true } = {}) {
  invalidateRouteRequests();
  const overlay = document.getElementById('routeTransition');
  if (!overlay || typeof renderRoute !== 'function') return renderRoute?.();

  const transitionId = ++routeTransitionSequence;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let loaderShownAt = 0;
  let loaderTimer = null;

  document.body.classList.add('route-transitioning');
  overlay.setAttribute('aria-hidden', 'false');
  overlay.classList.add('is-active');

  if (showLoader) {
    loaderTimer = window.setTimeout(() => {
      if (transitionId !== routeTransitionSequence) return;
      loaderShownAt = performance.now();
      overlay.classList.add('is-loading');
    }, ROUTE_LOADER_DELAY_MS);
  }

  await animateRouteOverlay(overlay, 1, reducedMotion ? 0 : ROUTE_COVER_DURATION_MS);
  if (transitionId !== routeTransitionSequence) return;

  try {
    await renderRoute();
    await waitForVisibleRouteMedia();
  } finally {
    if (loaderTimer) window.clearTimeout(loaderTimer);
    if (transitionId !== routeTransitionSequence) return;

    if (loaderShownAt && !reducedMotion) {
      const remainingLoaderTime = ROUTE_LOADER_MINIMUM_MS - (performance.now() - loaderShownAt);
      if (remainingLoaderTime > 0) await waitForRouteTransition(remainingLoaderTime);
    }

    await waitForRoutePaint();
    overlay.classList.remove('is-loading');
    await animateRouteOverlay(overlay, 0, reducedMotion ? 0 : ROUTE_REVEAL_DURATION_MS);
    if (transitionId !== routeTransitionSequence) return;
    overlay.classList.remove('is-active');
    overlay.style.removeProperty('opacity');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('route-transitioning');
  }
}

// Thread view state
let currentThreadMessageId = null;
let isThreadView = false;

function setupAboutLayerAnimation() {
  const illustration = document.querySelector('.about-layer-animation');
  const extensionLayer = illustration?.querySelector('.about-extension-layer');
  const plusButton = illustration?.querySelector('.about-extension-plus');
  const commentWindows = illustration
    ? [...illustration.querySelectorAll('.about-comment-window')]
    : [];
  if (!illustration || !extensionLayer || !plusButton || commentWindows.length !== 2) return;

  const duration = 6400;
  let animationFrame = null;
  let startedAt = 0;

  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => {
    const amount = clamp(value);
    return amount < 0.5
      ? 4 * amount * amount * amount
      : 1 - Math.pow(-2 * amount + 2, 3) / 2;
  };

  const draw = timestamp => {
    if (!startedAt) startedAt = timestamp;
    const phase = ((timestamp - startedAt) % duration) / duration;
    const floatProgress = (1 - Math.cos(phase * Math.PI * 2)) / 2;
    extensionLayer.style.transform = `translate3d(0, ${(-2 - floatProgress).toFixed(2)}px, ${(5 + (2 * floatProgress)).toFixed(2)}px)`;

    const getPressProgress = startPhase => {
      const localProgress = clamp((phase - startPhase) / 0.1);
      return localProgress <= 0.5
        ? ease(localProgress * 2)
        : ease((1 - localProgress) * 2);
    };
    const pressProgress = Math.max(getPressProgress(0.05), getPressProgress(0.38));
    plusButton.style.transform = `translate3d(0, 0, ${(-3 * pressProgress).toFixed(2)}px) scale(${(1 - (0.12 * pressProgress)).toFixed(3)})`;

    const exitProgress = ease(clamp((phase - 0.86) / 0.1));
    const enterRanges = [[0.17, 0.32], [0.5, 0.65]];

    commentWindows.forEach((commentWindow, index) => {
      const [startPhase, endPhase] = enterRanges[index];
      const enterProgress = ease(clamp((phase - startPhase) / (endPhase - startPhase)));
      const progress = enterProgress * (1 - exitProgress);
      const startX = index === 0 ? 22 : 28;
      const startY = index === 0 ? 10 : 14;
      const x = startX * (1 - progress);
      const y = startY * (1 - progress);
      const z = -34 * (1 - progress);
      const scale = 0.94 + (0.06 * progress);

      commentWindow.style.opacity = progress.toFixed(3);
      commentWindow.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, ${z.toFixed(2)}px) scale(${scale.toFixed(3)})`;
    });

    animationFrame = window.requestAnimationFrame(draw);
  };

  const start = () => {
    if (animationFrame !== null) return;
    startedAt = 0;
    animationFrame = window.requestAnimationFrame(draw);
  };

  const stop = () => {
    if (animationFrame === null) return;
    window.cancelAnimationFrame(animationFrame);
    animationFrame = null;
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) start();
      else stop();
    }, { threshold: 0.05 });
    observer.observe(illustration);
  } else {
    start();
  }
}

function setupAboutNetworkAnimation() {
  const illustration = document.querySelector('.about-network-animation');
  const scene = illustration?.querySelector('.about-network-scene');
  const links = illustration ? [...illustration.querySelectorAll('.about-network-link')] : [];
  const packetElements = illustration ? [...illustration.querySelectorAll('[data-network-packet]')] : [];
  const peerElements = illustration ? [...illustration.querySelectorAll('[data-network-peer]')] : [];
  const peers = new Map(peerElements.map(peer => [peer.dataset.networkPeer, peer]));
  const copies = new Map(peerElements.map(peer => [peer.dataset.networkPeer, peer.querySelector('[data-network-copy]')]));
  const routes = [
    { from: 'one', to: 'two', start: 0.19, end: 0.33 },
    { from: 'one', to: 'five', start: 0.21, end: 0.35 },
    { from: 'two', to: 'three', start: 0.42, end: 0.56 },
    { from: 'five', to: 'four', start: 0.44, end: 0.58 }
  ];

  if (!illustration || !scene || peers.size !== 5 || packetElements.length !== routes.length || [...copies.values()].some(copy => !copy)) return;

  const duration = 7500;
  const centers = new Map();
  const copyCenters = new Map();
  let animationFrame = null;
  let startedAt = 0;
  let geometryReady = false;

  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => {
    const amount = clamp(value);
    return amount < 0.5
      ? 4 * amount * amount * amount
      : 1 - Math.pow(-2 * amount + 2, 3) / 2;
  };

  const measure = () => {
    centers.clear();
    copyCenters.clear();
    peers.forEach((peer, name) => {
      centers.set(name, {
        x: peer.offsetLeft + (peer.offsetWidth / 2),
        y: peer.offsetTop + (peer.offsetHeight / 2)
      });
    });

    const getSceneCenter = element => {
      let x = element.offsetLeft + (element.offsetWidth / 2);
      let y = element.offsetTop + (element.offsetHeight / 2);
      let parent = element.offsetParent;

      while (parent && parent !== scene) {
        x += parent.offsetLeft;
        y += parent.offsetTop;
        parent = parent.offsetParent;
      }

      return { x, y };
    };

    copies.forEach((copy, name) => {
      copyCenters.set(name, getSceneCenter(copy));
    });

    geometryReady = scene.clientWidth > 0 && [...centers.values(), ...copyCenters.values()].every(point => point.x > 0 && point.y > 0);
    if (!geometryReady) return;

    links.forEach(link => {
      const from = centers.get(link.dataset.networkFrom);
      const to = centers.get(link.dataset.networkTo);
      if (!from || !to) return;
      const deltaX = to.x - from.x;
      const deltaY = to.y - from.y;
      link.style.left = `${from.x.toFixed(2)}px`;
      link.style.top = `${from.y.toFixed(2)}px`;
      link.style.width = `${Math.hypot(deltaX, deltaY).toFixed(2)}px`;
      link.style.transform = `rotate(${Math.atan2(deltaY, deltaX).toFixed(5)}rad)`;
    });
  };

  const draw = timestamp => {
    if (!startedAt) startedAt = timestamp;
    if (!geometryReady) measure();

    const phase = ((timestamp - startedAt) % duration) / duration;
    const exitFactor = 1 - ease(clamp((phase - 0.88) / 0.1));
    const received = new Map([...peers.keys()].map(name => [name, 0]));
    received.set('one', ease(clamp((phase - 0.06) / 0.08)));

    routes.forEach((route, index) => {
      const packet = packetElements[index];
      const from = copyCenters.get(route.from);
      const to = copyCenters.get(route.to);
      const localProgress = clamp((phase - route.start) / (route.end - route.start));
      const pathProgress = ease(localProgress);
      const packetVisibility = phase > route.start && phase < route.end
        ? Math.sin(Math.PI * localProgress) * exitFactor
        : 0;
      const arrivalProgress = ease(clamp((phase - route.end) / 0.045));
      received.set(route.to, Math.max(received.get(route.to) || 0, arrivalProgress));

      if (geometryReady && from && to) {
        const x = from.x + ((to.x - from.x) * pathProgress);
        const y = from.y + ((to.y - from.y) * pathProgress);
        packet.style.left = `${x.toFixed(2)}px`;
        packet.style.top = `${y.toFixed(2)}px`;
      }
      packet.style.opacity = packetVisibility.toFixed(3);
      packet.style.transform = `translate3d(-50%, -50%, 48px) scale(${(0.92 + (0.08 * packetVisibility)).toFixed(3)})`;
    });

    copies.forEach((copy, name) => {
      const progress = (received.get(name) || 0) * exitFactor;
      copy.style.opacity = progress.toFixed(3);
      copy.style.transform = `translateY(${(3 * (1 - progress)).toFixed(2)}px)`;
    });

    animationFrame = window.requestAnimationFrame(draw);
  };

  const start = () => {
    if (animationFrame !== null) return;
    startedAt = 0;
    geometryReady = false;
    measure();
    animationFrame = window.requestAnimationFrame(draw);
  };

  const stop = () => {
    if (animationFrame === null) return;
    window.cancelAnimationFrame(animationFrame);
    animationFrame = null;
  };

  window.addEventListener('resize', () => { geometryReady = false; }, { passive: true });

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) start();
      else stop();
    }, { threshold: 0.05 });
    observer.observe(illustration);
  } else {
    start();
  }
}

function setupAboutReputationAnimation() {
  const illustration = document.querySelector('.about-reputation-animation');
  const posts = illustration ? [...illustration.querySelectorAll('[data-score-level]')] : [];
  const primaryScore = illustration?.querySelector('[data-primary-score]');
  const thresholdTrack = illustration?.querySelector('.about-score-threshold-track');
  const thresholdFill = illustration?.querySelector('.about-score-threshold-fill');
  const thresholdKnob = illustration?.querySelector('.about-score-threshold-knob');
  const thresholdValue = illustration?.querySelector('[data-threshold-value]');

  if (!illustration || posts.length !== 3 || !primaryScore || !thresholdTrack || !thresholdFill || !thresholdKnob || !thresholdValue) return;

  const duration = 7600;
  let animationFrame = null;
  let startedAt = 0;

  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => {
    const amount = clamp(value);
    return amount < 0.5
      ? 4 * amount * amount * amount
      : 1 - Math.pow(-2 * amount + 2, 3) / 2;
  };

  const draw = timestamp => {
    if (!startedAt) startedAt = timestamp;
    const phase = ((timestamp - startedAt) % duration) / duration;

    const scorePulseLocal = clamp((phase - 0.14) / 0.22);
    const scorePulse = phase > 0.14 && phase < 0.36 ? Math.sin(Math.PI * scorePulseLocal) : 0;
    primaryScore.style.transform = `scale(${(1 + (0.14 * scorePulse)).toFixed(3)})`;

    const thresholdProgress = phase < 0.38
      ? 0
      : (phase < 0.72
        ? ease((phase - 0.38) / 0.34)
        : (phase < 0.86 ? 1 : ease((1 - phase) / 0.14)));
    const thresholdPosition = 3 + (94 * thresholdProgress);
    const thresholdLevel = 0.08 + (0.76 * thresholdProgress);
    const thresholdLabels = ['Very low', 'Low', 'Medium', 'High', 'Very high'];
    const thresholdIndex = Math.min(4, Math.round(thresholdProgress * 4));

    thresholdFill.style.width = `${thresholdPosition.toFixed(2)}%`;
    thresholdKnob.style.left = `${thresholdPosition.toFixed(2)}%`;
    thresholdValue.textContent = thresholdLabels[thresholdIndex];

    posts.forEach(post => {
      const scoreLevel = Number(post.dataset.scoreLevel || 0);
      const filteredProgress = ease(clamp((thresholdLevel - scoreLevel) / 0.1));
      if (filteredProgress < 0.999) post.hidden = false;
      post.style.opacity = (1 - filteredProgress).toFixed(3);
      post.style.transform = `translateY(${(3 * filteredProgress).toFixed(2)}px)`;
      if (filteredProgress >= 0.999) post.hidden = true;
    });

    animationFrame = window.requestAnimationFrame(draw);
  };

  const start = () => {
    if (animationFrame !== null) return;
    startedAt = 0;
    animationFrame = window.requestAnimationFrame(draw);
  };

  const stop = () => {
    if (animationFrame === null) return;
    window.cancelAnimationFrame(animationFrame);
    animationFrame = null;
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) start();
      else stop();
    }, { threshold: 0.05 });
    observer.observe(illustration);
  } else {
    start();
  }
}

function setupAboutLoopAnimation(illustration, duration, renderFrame) {
  if (!illustration) return;

  let animationFrame = null;
  let startedAt = 0;
  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => {
    const amount = clamp(value);
    return amount < 0.5
      ? 4 * amount * amount * amount
      : 1 - Math.pow(-2 * amount + 2, 3) / 2;
  };

  const draw = timestamp => {
    if (!startedAt) startedAt = timestamp;
    const phase = ((timestamp - startedAt) % duration) / duration;
    renderFrame(phase, ease, clamp);
    animationFrame = window.requestAnimationFrame(draw);
  };

  const start = () => {
    if (animationFrame !== null) return;
    startedAt = 0;
    animationFrame = window.requestAnimationFrame(draw);
  };

  const stop = () => {
    if (animationFrame === null) return;
    window.cancelAnimationFrame(animationFrame);
    animationFrame = null;
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) start();
      else stop();
    }, { threshold: 0.05 });
    observer.observe(illustration);
  } else {
    start();
  }
}

function getAboutElementCenter(element, root) {
  if (!element || !root) return { x: 0, y: 0 };
  let x = element.offsetLeft + (element.offsetWidth / 2);
  let y = element.offsetTop + (element.offsetHeight / 2);
  let parent = element.offsetParent;

  while (parent && parent !== root) {
    x += parent.offsetLeft;
    y += parent.offsetTop;
    parent = parent.offsetParent;
  }

  return { x, y };
}

function setupAboutIdentityAnimation() {
  const illustration = document.querySelector('.about-vault-animation');
  const scene = illustration?.querySelector('.about-vault-scene');
  const ownerHeading = illustration?.querySelector('.about-data-owner');
  const advertiserHeading = illustration?.querySelector('.about-advertiser-heading');
  const ownerRecipient = illustration?.querySelector('[data-owner-recipient]');
  const advertiserSource = illustration?.querySelector('[data-advertiser-source]');
  const storedData = illustration?.querySelector('[data-stored-data]');
  const receivedData = illustration?.querySelector('[data-received-data]');
  const adItems = illustration ? [...illustration.querySelectorAll('[data-ad-item]')] : [];
  const vaultDoor = illustration?.querySelector('[data-vault-door]');
  const vaultDial = illustration?.querySelector('.about-vault-dial');
  const paymentRail = illustration?.querySelector('[data-payment-rail]');
  const payment = illustration?.querySelector('[data-dollar-token]');
  const sharedData = illustration?.querySelector('[data-shared-data]');
  if (!illustration || !scene || !ownerHeading || !advertiserHeading || !ownerRecipient || !advertiserSource || !storedData || !receivedData || adItems.length !== 3 || !vaultDoor || !vaultDial || !paymentRail || !payment || !sharedData) return;

  const geometry = {};
  let geometryReady = false;

  const measure = () => {
    geometry.ownerRecipient = getAboutElementCenter(ownerRecipient, scene);
    geometry.advertiserSource = getAboutElementCenter(advertiserSource, scene);
    geometry.ownerHeading = getAboutElementCenter(ownerHeading, scene);
    geometry.advertiserHeading = getAboutElementCenter(advertiserHeading, scene);
    geometry.storedData = getAboutElementCenter(storedData, scene);
    geometry.receivedData = getAboutElementCenter(receivedData, scene);
    const paymentY = (geometry.ownerHeading.y + geometry.advertiserHeading.y) / 2;
    geometry.paymentStart = {
      x: geometry.advertiserHeading.x - (advertiserHeading.offsetWidth / 2) - 3,
      y: paymentY
    };
    geometry.paymentEnd = {
      x: geometry.ownerHeading.x + (ownerHeading.offsetWidth / 2) + 3,
      y: paymentY
    };
    geometryReady = scene.clientWidth > 0 && Object.values(geometry).every(point => point.x > 0 && point.y > 0);
    if (geometryReady) {
      const railX = geometry.paymentEnd.x - geometry.paymentStart.x;
      const railY = geometry.paymentEnd.y - geometry.paymentStart.y;
      paymentRail.style.width = `${Math.hypot(railX, railY).toFixed(2)}px`;
      paymentRail.style.transform = `translate3d(${geometry.paymentStart.x.toFixed(2)}px, ${(geometry.paymentStart.y - 0.75).toFixed(2)}px, 32px) rotate(${Math.atan2(railY, railX).toFixed(5)}rad)`;
    }
  };

  const placePacket = (element, from, to, progress, lift, z) => {
    const arc = 4 * progress * (1 - progress);
    const x = from.x + ((to.x - from.x) * progress) - (element.offsetWidth / 2);
    const y = from.y + ((to.y - from.y) * progress) - (element.offsetHeight / 2) - (lift * arc);
    element.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, ${(z + (8 * arc)).toFixed(2)}px)`;
  };

  window.addEventListener('resize', () => { geometryReady = false; }, { passive: true });

  setupAboutLoopAnimation(illustration, 9000, (phase, ease, clamp) => {
    if (!geometryReady) measure();
    const exit = 1 - ease(clamp((phase - 0.92) / 0.06));

    const paymentProgress = ease(clamp((phase - 0.04) / 0.38));
    const paymentEnter = ease(clamp((phase - 0.04) / 0.05));
    const paymentExit = 1 - ease(clamp((phase - 0.4) / 0.05));
    if (geometryReady) placePacket(payment, geometry.paymentStart, geometry.paymentEnd, paymentProgress, 0, 40);
    payment.style.opacity = (paymentEnter * paymentExit * exit).toFixed(3);

    const ownerPulse = phase > 0.4 && phase < 0.5
      ? Math.sin(Math.PI * clamp((phase - 0.4) / 0.1))
      : 0;
    ownerRecipient.style.transform = `translateZ(${(5 * ownerPulse).toFixed(2)}px) scale(${(1 + (0.08 * ownerPulse)).toFixed(3)})`;

    const dialProgress = ease(clamp((phase - 0.43) / 0.09));
    const doorEnter = ease(clamp((phase - 0.48) / 0.1));
    const doorClose = ease(clamp((phase - 0.91) / 0.08));
    const doorOpen = doorEnter * (1 - doorClose);
    vaultDial.style.transform = `translate(-50%, -50%) rotate(${(190 * dialProgress).toFixed(2)}deg)`;
    vaultDoor.style.transform = `rotateY(${(-66 * doorOpen).toFixed(2)}deg) translateZ(22px)`;

    const dataLocal = clamp((phase - 0.61) / 0.18);
    const dataProgress = ease(dataLocal);
    const dataVisible = phase > 0.6 && phase < 0.81 ? Math.sin(Math.PI * clamp((phase - 0.6) / 0.21)) : 0;
    if (geometryReady) placePacket(sharedData, geometry.storedData, geometry.receivedData, dataProgress, 9, 42);
    sharedData.style.opacity = dataVisible.toFixed(3);

    adItems.forEach((item, index) => {
      const activation = ease(clamp((phase - (0.76 + (index * 0.035))) / 0.08)) * exit;
      item.style.opacity = (0.54 + (0.46 * activation)).toFixed(3);
      item.style.transform = `translateZ(${(4 * activation).toFixed(2)}px) scale(${(1 + (0.015 * activation)).toFixed(3)})`;
    });
  });
}

function setupAboutOwnershipAnimation() {
  const illustration = document.querySelector('.about-ownership-animation');
  const scene = illustration?.querySelector('.about-ownership-scene');
  const sourceWallet = illustration?.querySelector('.about-owner-wallet-one');
  const receivingWallet = illustration?.querySelector('.about-owner-wallet-two');
  const spotToken = illustration?.querySelector('[data-ownership-token="spot"]');
  const usernameToken = illustration?.querySelector('[data-ownership-token="username"]');
  const ledgerBlocks = illustration ? [...illustration.querySelectorAll('[data-ledger-block]')] : [];
  const sourceSlots = {
    spot: sourceWallet?.querySelector('[data-wallet-slot="spot"]'),
    username: sourceWallet?.querySelector('[data-wallet-slot="username"]')
  };
  const receivingSlots = {
    spot: receivingWallet?.querySelector('[data-wallet-slot="spot"]'),
    username: receivingWallet?.querySelector('[data-wallet-slot="username"]')
  };
  if (!illustration || !scene || !sourceWallet || !receivingWallet || !sourceSlots.spot || !sourceSlots.username || !receivingSlots.spot || !receivingSlots.username || !spotToken || !usernameToken || ledgerBlocks.length !== 5) return;

  const geometry = {};
  let geometryReady = false;

  const centerInScene = element => {
    let x = element.offsetLeft + (element.offsetWidth / 2);
    let y = element.offsetTop + (element.offsetHeight / 2);
    let parent = element.offsetParent;

    while (parent && parent !== scene) {
      x += parent.offsetLeft + parent.clientLeft;
      y += parent.offsetTop + parent.clientTop;
      parent = parent.offsetParent;
    }

    return parent === scene ? { x, y } : null;
  };

  const measureToken = (name, token) => {
    const from = centerInScene(sourceSlots[name]);
    const to = centerInScene(receivingSlots[name]);
    if (!from || !to || token.offsetWidth <= 0 || token.offsetHeight <= 0) return false;

    token.style.left = `${(from.x - (token.offsetWidth / 2)).toFixed(2)}px`;
    token.style.top = `${(from.y - (token.offsetHeight / 2)).toFixed(2)}px`;
    token.classList.add('is-positioned');
    geometry[name] = { x: to.x - from.x, y: to.y - from.y };
    return true;
  };

  const measure = () => {
    geometryReady = scene.clientWidth > 0
      && measureToken('spot', spotToken)
      && measureToken('username', usernameToken);
  };

  window.addEventListener('resize', () => { geometryReady = false; }, { passive: true });

  const moveToken = (token, tokenGeometry, progress) => {
    const arc = Math.sin(Math.PI * progress);
    token.style.transform = `translate3d(${(tokenGeometry.x * progress).toFixed(2)}px, ${((tokenGeometry.y * progress) - (2 * arc)).toFixed(2)}px, ${(34 + (14 * arc)).toFixed(2)}px)`;
  };

  setupAboutLoopAnimation(illustration, 8000, (phase, ease, clamp) => {
    if (!geometryReady) measure();

    const transferProgress = (outStart, outEnd, returnStart, returnEnd) => {
      const outbound = ease(clamp((phase - outStart) / (outEnd - outStart)));
      const returning = ease(clamp((phase - returnStart) / (returnEnd - returnStart)));
      return outbound * (1 - returning);
    };

    const spotProgress = transferProgress(0.1, 0.3, 0.79, 0.91);
    const usernameProgress = transferProgress(0.35, 0.56, 0.73, 0.88);
    if (geometryReady) {
      moveToken(spotToken, geometry.spot, spotProgress);
      moveToken(usernameToken, geometry.username, usernameProgress);
    }

    ledgerBlocks.forEach((block, index) => {
      const firstRecord = ease(clamp((phase - (0.27 + (index * 0.045))) / 0.12));
      const secondRecord = ease(clamp((phase - (0.54 + (index * 0.04))) / 0.12));
      const recorded = Math.max(firstRecord, secondRecord) * (1 - ease(clamp((phase - 0.93) / 0.06)));
      block.style.setProperty('--ledger-ink-opacity', (0.24 + (0.76 * recorded)).toFixed(3));
      block.style.transform = `translateY(${(3 * (1 - recorded)).toFixed(2)}px)`;
    });
  });
}

function setupAboutRealWebAnimation() {
  const illustration = document.querySelector('.about-park-animation');
  const scene = illustration?.querySelector('.about-conversation-scene');
  const peopleGroup = illustration?.querySelector('.about-people-conversation');
  const botZone = illustration?.querySelector('.about-bot-zone');
  const people = new Map(
    illustration
      ? [...illustration.querySelectorAll('[data-park-person]')].map(person => [person.dataset.parkPerson, person])
      : []
  );
  const bots = new Map(
    illustration
      ? [...illustration.querySelectorAll('[data-park-bot]')].map(bot => [bot.dataset.parkBot, bot])
      : []
  );
  const messages = new Map(
    illustration
      ? [...illustration.querySelectorAll('[data-human-message]')].map(message => [message.dataset.humanMessage, message])
      : []
  );
  const botMessages = new Map(
    illustration
      ? [...illustration.querySelectorAll('[data-bot-message]')].map(message => [message.dataset.botMessage, message])
      : []
  );
  if (!illustration || !scene || !peopleGroup || !botZone || people.size !== 3 || bots.size !== 2 || messages.size !== 3 || botMessages.size !== 2) return;

  const humanRoutes = [
    { message: 'one', from: 'one', to: 'two', start: 0.1, end: 0.25 },
    { message: 'two', from: 'two', to: 'three', start: 0.29, end: 0.44 },
    { message: 'three', from: 'three', to: 'one', start: 0.48, end: 0.63 }
  ];
  const botRoutes = [
    { message: 'one', from: 'one', start: 0.16, end: 0.36, targetY: 0.34 },
    { message: 'two', from: 'two', start: 0.5, end: 0.7, targetY: 0.66 }
  ];
  const geometry = { people: new Map(), bots: new Map() };
  let geometryReady = false;

  const measure = () => {
    geometry.people.clear();
    geometry.bots.clear();
    people.forEach((person, name) => {
      const head = person.querySelector(':scope > i');
      if (!head) {
        geometry.people.set(name, { x: 0, headTop: -1 });
        return;
      }
      const center = getAboutElementCenter(head, peopleGroup);
      geometry.people.set(name, {
        x: center.x,
        headTop: center.y - (head.offsetHeight / 2)
      });
    });
    bots.forEach((bot, name) => geometry.bots.set(name, getAboutElementCenter(bot, botZone)));
    geometryReady = peopleGroup.clientWidth > 0
      && botZone.clientWidth > 0
      && [...geometry.people.values()].every(point => point.x > 0 && point.headTop >= 0)
      && [...geometry.bots.values()].every(point => point.x > 0 && point.y >= 0);
  };

  window.addEventListener('resize', () => { geometryReady = false; }, { passive: true });

  setupAboutLoopAnimation(illustration, 8000, (phase, ease, clamp) => {
    if (!geometryReady) measure();
    const exit = 1 - ease(clamp((phase - 0.9) / 0.08));
    const recipientPulse = new Map([...people.keys()].map(name => [name, 0]));

    humanRoutes.forEach(route => {
      const message = messages.get(route.message);
      const from = geometry.people.get(route.from);
      const to = geometry.people.get(route.to);
      const local = clamp((phase - route.start) / (route.end - route.start));
      const progress = ease(local);
      const enter = ease(clamp((phase - route.start) / 0.04));
      const visibility = enter * exit;
      const arc = 4 * progress * (1 - progress);

      if (geometryReady && from && to) {
        const x = from.x + ((to.x - from.x) * progress) - (message.offsetWidth / 2);
        const headTop = from.headTop + ((to.headTop - from.headTop) * progress);
        const y = headTop - message.offsetHeight - 10 - (11 * arc);
        message.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, ${(38 + (8 * arc)).toFixed(2)}px)`;
      }
      message.style.opacity = visibility.toFixed(3);

      const pulse = phase > route.end && phase < route.end + 0.09
        ? Math.sin(Math.PI * clamp((phase - route.end) / 0.09))
        : 0;
      recipientPulse.set(route.to, Math.max(recipientPulse.get(route.to) || 0, pulse));
    });

    people.forEach((person, name) => {
      const pulse = recipientPulse.get(name) || 0;
      const ambient = Math.sin((phase * Math.PI * 2) + ([...people.keys()].indexOf(name) * 1.7));
      person.style.transform = `translate3d(0, ${(-1.4 * pulse - (0.35 * ambient)).toFixed(2)}px, 24px)`;
    });

    botRoutes.forEach(route => {
      const message = botMessages.get(route.message);
      const from = geometry.bots.get(route.from);
      const local = clamp((phase - route.start) / (route.end - route.start));
      const progress = ease(clamp(local / 0.84));
      const enter = ease(clamp(local / 0.16));
      const fallShort = 1 - ease(clamp((local - 0.52) / 0.25));
      const visibility = enter * fallShort * exit;

      if (geometryReady && from) {
        const target = { x: -6, y: botZone.clientHeight * route.targetY };
        const arc = 4 * progress * (1 - progress);
        const x = from.x + ((target.x - from.x) * progress) - (message.offsetWidth / 2);
        const y = from.y + ((target.y - from.y) * progress) - (message.offsetHeight / 2) - (5 * arc);
        message.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, ${(26 + (5 * arc)).toFixed(2)}px) scale(${(0.96 + (0.04 * fallShort)).toFixed(3)})`;
      }
      message.style.opacity = visibility.toFixed(3);
    });
  });
}

// Initialize the application
document.addEventListener('DOMContentLoaded', async () => {
  setupTheme();
  setupAboutLayerAnimation();
  setupAboutNetworkAnimation();
  setupAboutReputationAnimation();
  setupAboutIdentityAnimation();
  setupAboutOwnershipAnimation();
  setupAboutRealWebAnimation();
  setupSortMenu();
  await checkAuthStatus();
  await renderRouteFromLocation();
  updateHomepageFeedPreviewState();

  setupIntersectionObserver(() => loadFeed(false), 'loading-indicator');
  setupSearch();
  setupFormListeners();
});

let homepageFeedPreviewFrame = null;
let homepageIntroScaleFrame = null;
let mobileChromeLastScrollY = Math.max(0, window.scrollY);
let mobileChromeContext = '';
let mobileChromeHomepageJoinScrollY = 0;
let mobileChromeMetricsDirty = true;
let mobileChromeOffset = 0;
let mobileChromeMaximumOffset = 0;
let mobileChromeStack = null;
let mobileChromeMountRecords = [];
let mobileHomepageChromeTargets = [];
let mobileHomepageChromeOffset = 0;
let desktopChromeLastScrollY = Math.max(0, window.scrollY);
let desktopChromeContext = '';
let desktopChromeTargets = [];
let desktopChromeOffset = 0;
let desktopChromeMaximumOffset = 0;

function updateHomepageIntroScale() {
  homepageIntroScaleFrame = null;
  const intro = document.querySelector('.sidebar-intro-content');
  const sidebar = intro?.closest('.sidebar');
  const main = document.querySelector('.main-column');
  const shouldScale = Boolean(
    intro &&
    sidebar &&
    main &&
    document.body.classList.contains('homepage-layout') &&
    window.innerWidth <= 680 &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  if (!shouldScale) {
    intro?.style.removeProperty('--homepage-intro-scale');
    return;
  }

  const sidebarRect = sidebar.getBoundingClientRect();
  const mainRect = main.getBoundingClientRect();
  const overlap = Math.max(0, Math.min(sidebarRect.height, sidebarRect.bottom - mainRect.top));
  const progress = overlap / Math.max(1, sidebarRect.height);
  const scale = 1 - (progress * 0.035);
  intro.style.setProperty('--homepage-intro-scale', scale.toFixed(4));
}

function scheduleHomepageIntroScale() {
  if (homepageIntroScaleFrame !== null) return;
  homepageIntroScaleFrame = window.requestAnimationFrame(updateHomepageIntroScale);
}

function getDocumentOffsetTop(element) {
  let top = 0;
  let current = element;
  while (current) {
    top += current.offsetTop || 0;
    current = current.offsetParent;
  }
  return top;
}

function getMobileChromeStack() {
  if (mobileChromeStack) return mobileChromeStack;
  mobileChromeStack = document.createElement('div');
  mobileChromeStack.className = 'mobile-chrome-stack';
  mobileChromeStack.id = 'mobileChromeStack';
  mobileChromeStack.hidden = true;
  document.body.insertBefore(mobileChromeStack, document.querySelector('.navbar'));
  return mobileChromeStack;
}

function getMobileChromeSourceTop(element) {
  const record = mobileChromeMountRecords.find(item => item.element === element);
  return getDocumentOffsetTop(record?.placeholder || element);
}

function applyMobileChromeOffset(offset) {
  const nextOffset = Math.min(mobileChromeMaximumOffset, Math.max(0, offset));
  if (Math.abs(nextOffset - mobileChromeOffset) < 0.001) return;
  mobileChromeOffset = nextOffset;
  if (mobileChromeStack) {
    mobileChromeStack.style.transform = `translate3d(0, ${-mobileChromeOffset}px, 0)`;
  }
}

function mountMobileChrome(elements) {
  const stack = getMobileChromeStack();
  if (mobileChromeMountRecords.length) return;

  mobileChromeMountRecords = elements.filter(Boolean).map(element => {
    const placeholder = document.createElement('div');
    placeholder.className = `mobile-chrome-placeholder mobile-chrome-placeholder-${element.classList[0] || 'item'}`;
    placeholder.style.height = `${element.offsetHeight}px`;
    placeholder.setAttribute('aria-hidden', 'true');
    element.before(placeholder);
    stack.appendChild(element);
    return { element, placeholder };
  });

  stack.hidden = false;
  document.body.classList.add('mobile-chrome-mounted');
  mobileChromeMaximumOffset = stack.querySelector('.navbar')?.offsetHeight || 64;
  mobileChromeOffset = 0;
  stack.style.transform = 'translate3d(0, 0, 0)';
}

function unmountMobileChrome() {
  if (!mobileChromeMountRecords.length) return;
  mobileChromeMountRecords.forEach(({ element, placeholder }) => placeholder.replaceWith(element));
  mobileChromeMountRecords = [];
  mobileChromeOffset = 0;
  mobileChromeMaximumOffset = 0;
  if (mobileChromeStack) {
    mobileChromeStack.style.removeProperty('transform');
    mobileChromeStack.hidden = true;
  }
  document.body.classList.remove('mobile-chrome-mounted');
}

function resetMobileHomepageChrome() {
  mobileHomepageChromeTargets.forEach(element => element.style.removeProperty('transform'));
  mobileHomepageChromeTargets = [];
  mobileHomepageChromeOffset = 0;
}

function applyMobileHomepageChromeOffset(offset) {
  const navbarHeight = document.querySelector('.navbar')?.offsetHeight || 64;
  const nextOffset = Math.min(navbarHeight, Math.max(0, offset));
  mobileHomepageChromeOffset = nextOffset;
  const transform = `translate3d(0, ${-nextOffset}px, 0)`;
  mobileHomepageChromeTargets.forEach(element => { element.style.transform = transform; });
}

function resetDesktopChrome() {
  desktopChromeTargets.forEach(element => element.style.removeProperty('transform'));
  desktopChromeContext = '';
  desktopChromeTargets = [];
  desktopChromeOffset = 0;
  desktopChromeMaximumOffset = 0;
}

function updateDesktopChrome(currentScrollY) {
  const feed = document.querySelector('.feed');
  const thread = document.getElementById('threadView');
  const feedVisible = Boolean(feed && feed.style.display !== 'none');
  const threadVisible = Boolean(thread && thread.style.display !== 'none');
  const activeHeader = threadVisible
    ? document.querySelector('.thread-view-header')
    : (feedVisible ? document.querySelector('.feed-header') : null);
  const activeSurface = activeHeader?.querySelector('.thread-view-header-head, .feed-header-head') || null;
  const navbar = document.querySelector('.navbar');
  const urlPreview = document.getElementById('urlPreviewBar');
  const urlPreviewVisible = Boolean(urlPreview && !urlPreview.classList.contains('hidden'));

  if (!activeHeader || !activeSurface || !navbar) {
    resetDesktopChrome();
    desktopChromeLastScrollY = currentScrollY;
    return;
  }

  const nextContext = `${threadVisible ? 'post' : 'feed'}:${urlPreviewVisible ? 'preview' : 'plain'}`;
  if (nextContext !== desktopChromeContext) {
    resetDesktopChrome();
    desktopChromeContext = nextContext;
    desktopChromeTargets = [navbar, urlPreviewVisible ? urlPreview : null, activeSurface].filter(Boolean);
    desktopChromeMaximumOffset = navbar.offsetHeight;
    desktopChromeOffset = Math.min(desktopChromeMaximumOffset, currentScrollY);
  } else {
    const scrollDelta = currentScrollY - desktopChromeLastScrollY;
    desktopChromeOffset = Math.min(
      desktopChromeMaximumOffset,
      Math.max(0, desktopChromeOffset + scrollDelta)
    );
  }

  const transform = `translate3d(0, ${-desktopChromeOffset}px, 0)`;
  desktopChromeTargets.forEach(element => { element.style.transform = transform; });
  desktopChromeLastScrollY = currentScrollY;
}

function updateHomepageFeedPreviewState() {
  homepageFeedPreviewFrame = null;
  const currentScrollY = Math.max(0, window.scrollY);
  // Website chrome stays visible on every route. Clear any transforms or
  // reparented bars left by an earlier route/render and let CSS sticky
  // positioning keep Navbar, URL preview, and Feed/Page/Post bars in place.
  unmountMobileChrome();
  resetMobileHomepageChrome();
  resetDesktopChrome();
  document.body.classList.remove('homepage-feed-expanded');
  mobileChromeLastScrollY = currentScrollY;
  desktopChromeLastScrollY = currentScrollY;
  scheduleHomepageIntroScale();
}

function scheduleHomepageFeedPreviewState() {
  if (homepageFeedPreviewFrame !== null) return;
  homepageFeedPreviewFrame = window.requestAnimationFrame(updateHomepageFeedPreviewState);
}

window.addEventListener('resize', () => {
  mobileChromeMetricsDirty = true;
  scheduleHomepageFeedPreviewState();
  scheduleHomepageIntroScale();
});

window.addEventListener('scroll', scheduleHomepageIntroScale, { passive: true });

document.addEventListener('error', event => {
  const image = event.target;
  if (image instanceof HTMLImageElement && image.matches('.message-avatar img, .profile-navbar-avatar img, .people-avatar img, .search-result-avatar img, .search-result-preview')) {
    image.remove();
  }
}, true);

// Browser history can include direct-entry states without app metadata, so the
// URL is the source of truth when Back or Forward is used.
window.addEventListener('popstate', () => {
  void runRouteTransition(() => renderRouteFromLocation());
});

async function renderRouteFromLocation() {
  const pathname = window.location.pathname;
  const legalRoute = pathname.match(/^\/(terms|privacy)\/?$/);
  if (legalRoute) {
    setJoinPageActive(false);
    setAboutPageActive(false);
    await showLegalPage(legalRoute[1], false);
    return;
  }
  setLegalPageActive(null);
  if (pathname === '/about') {
    setJoinPageActive(false);
    await showAboutPage(false);
    return;
  }
  setAboutPageActive(false);
  if (pathname === '/join') {
    await showJoinPage(false);
    return;
  }
  setJoinPageActive(false);
  if (pathname === '/search') {
    const params = new URLSearchParams(window.location.search);
    await showSearchView(params.get('q') || '', params.get('tab') || 'page', false);
    return;
  }
  const pathMatchMessage       = pathname.match(/^\/m\/([^/]+)$/);
  const pathMatchProfileRelationships = pathname.match(/^\/u\/([^/]+)\/(following|followers)$/);
  const pathMatchProfileDetails = pathname.match(/^\/u\/([^/]+)\/details$/);
  const pathMatchProfile       = pathname.match(/^\/u\/([^/]+)$/);
  const pathMatchChannel       = pathname.match(/^\/c\/([^/]+)$/);
  const pathMatchPage          = pathname.match(/^\/p\/([^/]+)$/);
  const pathMatchUnencodedPage = pathname.match(/^\/p\/(.+)$/);

  if (pathMatchMessage) {
    await showThreadView(decodeURIComponent(pathMatchMessage[1]), false);
    return;
  }
  if (pathMatchProfileRelationships) {
    const loading = showProfileView(decodeURIComponent(pathMatchProfileRelationships[1]), false);
    const routeGeneration = activeRouteGeneration;
    await loading;
    if (routeGeneration !== activeRouteGeneration) return;
    await showProfileRelationships(pathMatchProfileRelationships[2], false);
    return;
  }
  if (pathMatchProfileDetails) {
    const loading = showProfileView(decodeURIComponent(pathMatchProfileDetails[1]), false);
    const routeGeneration = activeRouteGeneration;
    await loading;
    if (routeGeneration !== activeRouteGeneration) return;
    showProfileDetails(false);
    return;
  }
  if (pathMatchProfile) {
    await showProfileView(decodeURIComponent(pathMatchProfile[1]), false);
    return;
  }
  if (pathMatchChannel) {
    await showChannelView(decodeURIComponent(pathMatchChannel[1]), false);
    return;
  }
  if (pathMatchPage || pathMatchUnencodedPage) {
    const raw = (pathMatchPage || pathMatchUnencodedPage)[1];
    const decodedUrl = decodeURIComponent(raw);
    const looksUnencoded = raw !== encodeURIComponent(decodedUrl);

    if (looksUnencoded) {
      const newPath = `/p/${encodeURIComponent(raw)}${window.location.search}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', newPath);
    }
    await showPageFeed(decodedUrl);
    return;
  }

  await showFeedView();
}

// Setup form submit listeners
function setupFormListeners() {
  const loginForm = document.getElementById('loginForm');
  if(loginForm) {
    loginForm.addEventListener('submit', handleLogin);
  }
  
  // Register form
  const registerForm = document.getElementById('registerForm');
  if(registerForm) {
    registerForm.addEventListener('submit', handleRegister);
  }
  
  // Activation form
  const activationForm = document.getElementById('activationForm');
  if(activationForm) {
    activationForm.addEventListener('submit', handleActivation);
  }

  const reportForm = document.getElementById('reportForm');
  if (reportForm) reportForm.addEventListener('submit', submitReport);
  
  // Preferences form
  const preferencesForm = document.getElementById('preferencesForm');
  if(preferencesForm) {
    preferencesForm.addEventListener('submit', handlePreferences);
  }
  
  // Preferences slider
  const minScoreSlider = document.getElementById('minScore');
  if(minScoreSlider) {
    minScoreSlider.addEventListener('input', updateScoreValue);
  }
}

// Check if user is authenticated
async function checkAuthStatus() {
  try {
    const data = await apiCheckAuthStatus();
    if(data.loggedIn) {
      isLoggedIn = true;
      await loadProfile();
    }
  } catch (error) {
    console.error('Error checking auth status:', error);
  } finally {
    updateNavigation();
  }
}

// Load user profile
async function loadProfile() {
  try {
    const data = await apiLoadProfile();
    currentUser = data.user;
    if(data.user.preferences) {
      userPreferences.verifiedOnly = data.user.preferences.verifiedOnly || false;
      userPreferences.minScore = data.user.preferences.minScore || 0;
      userPreferences.defaultSortingMethod = data.user.preferences.defaultSortingMethod || 'new';

      // Update the HTML dropdown for the default sorting method if it exists
      const sortingMethodSelect = document.getElementById('sortingMethod');
      if (sortingMethodSelect) {
        sortingMethodSelect.value = userPreferences.defaultSortingMethod;
        syncSortMenu(userPreferences.defaultSortingMethod);
      }
    }
    displayProfile();
  } catch (error) {
    console.error('Error loading profile:', error);
  }
}

// Display profile in sidebar
function displayProfile() {
  const profileSidebar = document.getElementById('profileSidebar');
  const profileContent = document.getElementById('sidebarProfileContent');
  
  if(currentUser) {
    profileSidebar.style.display = 'block';
    const domains = Array.isArray(currentUser.domains) ? currentUser.domains : [];
    const profileDomains = domains.map(d => escapeHtml(d.domain));
    let domainsHtml = '';
    if(profileDomains.length > 0) {
      domainsHtml = '<div class="profile-info-item"><label>Domains</label>';
      for(const d of domains) {
        domainsHtml += `<div class="value">${escapeHtml(d.domain)} ${d.verified ? '(verified)' : '(unverified)'}</div>`;
      }
      domainsHtml += '</div>';
    }

    profileContent.innerHTML = `
      <div class="profile-info">
        <div class="profile-info-item">
          <label>Full Name</label>
          <div class="value">${escapeHtml(currentUser.fullName)}</div>
        </div>
        <div class="profile-info-item">
          <label>Display Name</label>
          <div class="value">@${escapeHtml(currentUser.displayName)}</div>
        </div>
        <div class="profile-info-item">
          <label>Email</label>
          <div class="value">${escapeHtml(currentUser.email)}</div>
        </div>
        <div class="profile-info-item">
          <label>Decentralized reputation</label>
          <div class="value">${escapeHtml(String(currentUser.reputation ?? 0))}</div>
        </div>
        <div class="profile-info-item">
          <label>Platform reputation</label>
          <div class="value">${escapeHtml(String(currentUser.platformReputation ?? 0))}</div>
        </div>
        <div class="profile-info-item">
          <label>Linked pubkey</label>
          <div class="value">${currentUser.pubkey ? `${escapeHtml(currentUser.pubkey.slice(0,4))}...${escapeHtml(currentUser.pubkey.slice(-4))}` : `<button class="btn btn-primary" id="linkAccount">Link account</button>`}</div>
        </div>
        ${domainsHtml}
      </div>
      <button class="btn btn-danger btn-block" id="logoutBtn">Logout</button>
    `;
  }
}

// Update navigation based on auth status
function updateNavigation() {
  const sidebarActions = document.getElementById('sidebarActions');
  const sidebarLegalCopy = document.getElementById('sidebarLegalCopy');
  const mobileJoinFooter = document.getElementById('mobileJoinFooter');
  const followingOption = document.getElementById('followingSortOption');
  if (sidebarLegalCopy) sidebarLegalCopy.hidden = isLoggedIn;
  if (mobileJoinFooter) mobileJoinFooter.hidden = isLoggedIn;
  if (followingOption) {
    followingOption.disabled = !isLoggedIn;
    followingOption.hidden = !isLoggedIn;
  }
  
  if(isLoggedIn && currentUser) {
    sidebarActions.innerHTML = `
      <div class="profile-dropdown" id="profileDropdown">
        <button class="profile-dropdown-btn btn-block" id="profileDropdownBtn">Profile <i class="fa-light fa-chevron-down" aria-hidden="true"></i></button>
        <div class="profile-dropdown-content">
          <button id="preferencesBtn">Preferences</button>
          <button id="navLogoutBtn">Logout</button>
        </div>
      </div>
    `;
  } else {
    sidebarActions.innerHTML = '<button class="btn btn-primary btn-block" id="registerBtn" type="button">Join Kekko</button><a class="learn-more-link" href="/about">Learn more</a>';
    if (userPreferences.defaultSortingMethod === 'following') {
      userPreferences.defaultSortingMethod = 'new';
      const sortingMethod = document.getElementById('sortingMethod');
      if (sortingMethod) {
        sortingMethod.value = 'new';
        syncSortMenu('new');
      }
    }
  }
}

// Load channels — delegates to shared/js/feed.js
function refreshChannels() {
  loadChannels({ apiBaseUrl: API_BASE_URL, currentChannel });
}

function displayFeedView() {
  deactivateStandalonePages();
  isThreadView = false;
  currentThreadMessageId = null;
  currentUserProfile = null;

  const feedEl = document.querySelector('.feed');
  const threadEl = document.getElementById('threadView');
  const profileViewEl = document.getElementById('profileView');
  const searchViewEl = document.getElementById('searchView');
  if (feedEl) feedEl.style.display = '';
  if (threadEl) threadEl.style.display = 'none';
  if (profileViewEl) profileViewEl.style.display = 'none';
  if (searchViewEl) searchViewEl.style.display = 'none';
}

function resetFeedPagination() {
  messages = [];
  currentPagePostCount = null;
  currentPageAverageRating = null;
  currentPageSummaryError = '';
  hasMore = true;
  lastMessageTimestamp = null;
}

function getMessagePageUrl(message) {
  return String(message?.canonicalUrl || message?.url || '').trim();
}

function drainPageMetadataJobs() {
  while (activePageMetadataJobs < 2 && pageMetadataJobs.length) {
    const job = pageMetadataJobs.shift();
    if (job.routeGeneration !== activeRouteGeneration || job.loadGeneration !== feedLoadGeneration) {
      job.resolve(null);
      continue;
    }
    activePageMetadataJobs += 1;
    void (async () => {
      try { job.resolve(await job.run()); }
      catch (error) { job.reject(error); }
      finally {
        activePageMetadataJobs -= 1;
        drainPageMetadataJobs();
      }
    })();
  }
}

async function fetchPagePostMetadata(pageUrl) {
  const now = Date.now();
  const cached = pagePostMetadataCache.get(pageUrl);
  if (cached && cached.expiresAt > now) return cached.promise;
  const routeGeneration = activeRouteGeneration;
  const loadGeneration = feedLoadGeneration;
  const preferenceFilters = { verifiedOnly: userPreferences.verifiedOnly, minScore: userPreferences.minScore };
  const promise = new Promise((resolve, reject) => {
    pageMetadataJobs.push({ routeGeneration, loadGeneration, resolve, reject, run: async () => {
      const ratingsById = new Map();
      let skip = 0;
      let hasMorePages = true;
      while (hasMorePages) {
        if (routeGeneration !== activeRouteGeneration || loadGeneration !== feedLoadGeneration) return null;
        const params = new URLSearchParams({
          url: pageUrl, limit: String(PAGE_POST_METADATA_BATCH_SIZE),
          skip: String(skip), sortingMethod: 'old'
        });
        if (preferenceFilters.verifiedOnly) params.set('verifiedOnly', 'true');
        if (preferenceFilters.minScore !== null) params.set('minScore', String(preferenceFilters.minScore));
        const data = await apiFetchFeed(params);
        if (routeGeneration !== activeRouteGeneration || loadGeneration !== feedLoadGeneration) return null;
        const pageMessages = readFeedMessages(data);
        const previousSize = ratingsById.size;
        for (const message of pageMessages) {
          if (!message?.messageId) throw new Error('Page response is missing a message ID');
          ratingsById.set(String(message.messageId), getMessageRating(message));
        }
        hasMorePages = data.hasMore === true;
        if (hasMorePages && ratingsById.size === previousSize) {
          throw new Error('Page pagination did not advance');
        }
        skip += pageMessages.length;
      }
      const ratings = [...ratingsById.values()].filter(rating => rating > 0);
      return {
        total: ratingsById.size,
        averageRating: ratings.length ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length : 0
      };
    }});
    drainPageMetadataJobs();
  });
  pagePostMetadataCache.set(pageUrl, { promise, expiresAt: now + PAGE_POST_METADATA_TTL_MS });
  try {
    const metadata = await promise;
    if (!metadata && pagePostMetadataCache.get(pageUrl)?.promise === promise) pagePostMetadataCache.delete(pageUrl);
    return metadata;
  } catch (error) {
    if (pagePostMetadataCache.get(pageUrl)?.promise === promise) pagePostMetadataCache.delete(pageUrl);
    throw error;
  }
}

function readFeedMessages(data) {
  if (Array.isArray(data?.messages)) return data.messages;
  if (data?.message === 'Nothing to show.') return [];
  throw new Error('Invalid feed response');
}

function patchMessagePageCounts(pageMessages) {
  const byId = new Map(pageMessages.map(message => [String(message.messageId), message]));
  document.querySelectorAll('.message-card').forEach(card => {
    const message = byId.get(card.dataset.messageId);
    if (!message) return;
    card.querySelectorAll('.source-post-count').forEach(element => {
      const count = message._pagePostCount;
      element.classList.toggle('hidden', !Number.isInteger(count) || count <= 0);
      element.setAttribute('aria-label', `${count} ${count === 1 ? 'post' : 'posts'}`);
      element.lastElementChild.textContent = String(count);
    });
  });
}

async function hydratePagePostMetadata(pageMessages = [], { summaryPageUrl = '' } = {}) {
  const routeGeneration = activeRouteGeneration;
  const loadGeneration = feedLoadGeneration;
  const messagesByPage = new Map();
  for (const message of pageMessages) {
    const pageUrl = getMessagePageUrl(message);
    if (!pageUrl || !message?.messageId || message.inReplyTo) continue;
    if (!messagesByPage.has(pageUrl)) messagesByPage.set(pageUrl, []);
    messagesByPage.get(pageUrl).push(message);
  }
  if (summaryPageUrl && !messagesByPage.has(summaryPageUrl)) messagesByPage.set(summaryPageUrl, []);
  let changed = false;
  await Promise.all([...messagesByPage.entries()].map(async ([pageUrl, groupedMessages]) => {
    try {
      const metadata = await fetchPagePostMetadata(pageUrl);
      if (!metadata || routeGeneration !== activeRouteGeneration || loadGeneration !== feedLoadGeneration) return;
      for (const message of groupedMessages) {
        message._pagePostCount = metadata.total;
        message._pageAverageRating = metadata.averageRating;
      }
      patchMessagePageCounts(groupedMessages);
      if (pageUrl === summaryPageUrl) {
        currentPagePostCount = metadata.total;
        currentPageAverageRating = metadata.averageRating;
        currentPageSummaryError = '';
        updatePageFeedSummary();
      }
      changed = true;
    } catch (error) {
      if (routeGeneration !== activeRouteGeneration || loadGeneration !== feedLoadGeneration) return;
      console.warn('Unable to load page post metadata:', pageUrl, error);
      if (pageUrl === summaryPageUrl) {
        currentPageSummaryError = 'Page activity unavailable';
        updatePageFeedSummary();
      }
    }
  }));
  return changed;
}

function taggedUsersForMessage(message = {}) {
  if (Array.isArray(message.taggedUsers) && message.taggedUsers.length) return message.taggedUsers;
  return Array.isArray(message.taggedUserInfo) ? message.taggedUserInfo : [];
}

function taggedUserPubkey(taggedUser = {}) {
  return String(taggedUser.pubkey || taggedUser.delegatePubkey || taggedUser.userPubkey || '').trim();
}

async function fetchMentionProfile(pubkey) {
  const key = String(pubkey || '').trim();
  if (!key) return null;
  const now = Date.now();
  const cached = mentionProfileCache.get(key);
  if (cached && cached.expiresAt > now) return cached.promise;

  const promise = (async () => {
    const response = await fetch(`${API_BASE_URL}/user/profile/${encodeURIComponent(key)}`, { credentials: 'include' });
    if (!response.ok) return null;
    return response.json();
  })();
  mentionProfileCache.set(key, { promise, expiresAt: now + MENTION_PROFILE_TTL_MS });
  try {
    return await promise;
  } catch (error) {
    if (mentionProfileCache.get(key)?.promise === promise) mentionProfileCache.delete(key);
    throw error;
  }
}

async function hydrateMessageMentions(messageList = []) {
  const mentionsByPubkey = new Map();
  let changed = false;
  const currentPubkey = getPersonPubkey(currentUser || {});

  for (const message of Array.isArray(messageList) ? messageList : []) {
    for (const taggedUser of taggedUsersForMessage(message)) {
      const pubkey = taggedUserPubkey(taggedUser);
      if (!pubkey) continue;
      if (currentPubkey && pubkey === currentPubkey) {
        const userName = getPublicUsername(currentUser || {});
        if (userName && taggedUser.userName !== userName) {
          taggedUser.userName = userName;
          changed = true;
        }
        continue;
      }
      if (!mentionsByPubkey.has(pubkey)) mentionsByPubkey.set(pubkey, []);
      mentionsByPubkey.get(pubkey).push(taggedUser);
    }
  }

  await Promise.all([...mentionsByPubkey.entries()].map(async ([pubkey, taggedUsers]) => {
    try {
      const profile = await fetchMentionProfile(pubkey);
      const userName = getPublicUsername(profile || {});
      if (!userName) return;
      for (const taggedUser of taggedUsers) {
        if (taggedUser.userName === userName) continue;
        taggedUser.userName = userName;
        changed = true;
      }
    } catch (error) {
      console.warn('Unable to load mention profile:', pubkey, error);
    }
  }));

  return changed;
}

function formatPageDisplayUrl(value) {
  const rawUrl = String(value || '').trim();
  if (!rawUrl) return '';

  try {
    const parsed = new URL(rawUrl);
    const hostname = parsed.hostname.replace(/^www\./i, '');
    return `${hostname}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return rawUrl.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  }
}

function getPagePreviewTitle(pageUrl, sourceMessage = null) {
  const pageMessage = sourceMessage || messages.find(message => {
    return String(
      message?.pageTitle
      || message?.urlInfo?.title
      || message?.socialCard?.title
      || ''
    ).trim();
  });
  const messageTitle = pageMessage?.pageTitle
    || pageMessage?.urlInfo?.title
    || pageMessage?.socialCard?.title;
  if (String(messageTitle || '').trim()) return decodeHtmlEntities(messageTitle).trim();

  try {
    return new URL(pageUrl).hostname.replace(/^www\./i, '');
  } catch {
    return formatPageDisplayUrl(pageUrl) || 'Page';
  }
}

function getPagePreviewImage(sourceMessage = null) {
  const pageMessage = sourceMessage || messages.find(message => {
    return String(
      message?.socialCard?.image
      || message?.urlInfo?.image
      || message?.urlInfo?.imageUrl
      || message?.urlInfo?.imageURL
      || ''
    ).trim();
  });

  return String(
    pageMessage?.socialCard?.image
    || pageMessage?.urlInfo?.image
    || pageMessage?.urlInfo?.imageUrl
    || pageMessage?.urlInfo?.imageURL
    || ''
  ).trim();
}

function isPagePreviewVerified(sourceMessage = null) {
  const pageMessage = sourceMessage || messages[0] || {};
  return Boolean(
    pageMessage?.urlInfo?.verified
    || pageMessage?.urlInfo?.isVerified
    || pageMessage?.socialCard?.verified
  );
}

function updateUrlPreviewBar(pageUrl = '', title = '', imageUrl = '', verified = false) {
  const mainColumn = document.getElementById('mainContent');
  const bar = document.getElementById('urlPreviewBar');
  const imageElement = document.getElementById('urlPreviewImage');
  const titleElement = document.getElementById('urlPreviewTitle');
  const verifiedElement = document.getElementById('urlPreviewVerified');
  const urlElement = document.getElementById('urlPreviewUrl');
  const normalizedUrl = String(pageUrl || '').trim();
  if (!mainColumn || !bar || !imageElement || !titleElement || !verifiedElement || !urlElement) return;

  if (!normalizedUrl) {
    mainColumn.classList.remove('has-url-preview');
    bar.classList.add('hidden');
    bar.classList.remove('has-no-image');
    imageElement.removeAttribute('src');
    imageElement.classList.remove('hidden');
    titleElement.textContent = '';
    verifiedElement.classList.add('hidden');
    urlElement.textContent = '';
    urlElement.removeAttribute('href');
    scheduleHomepageFeedPreviewState();
    return;
  }

  titleElement.textContent = String(title || getPagePreviewTitle(normalizedUrl)).trim();
  verifiedElement.classList.toggle('hidden', !verified);
  urlElement.textContent = formatPageDisplayUrl(normalizedUrl);
  urlElement.href = normalizedUrl;
  const normalizedImageUrl = String(imageUrl || '').trim();
  const hideImage = () => {
    imageElement.removeAttribute('src');
    imageElement.classList.add('hidden');
    bar.classList.add('has-no-image');
  };
  imageElement.onerror = hideImage;
  if (normalizedImageUrl) {
    imageElement.classList.remove('hidden');
    bar.classList.remove('has-no-image');
    imageElement.src = normalizedImageUrl;
  } else {
    hideImage();
  }
  bar.classList.remove('hidden');
  mainColumn.classList.add('has-url-preview');
  scheduleHomepageFeedPreviewState();
}

function updatePageFeedSummary() {
  const summary = document.getElementById('pageFeedSummary');
  const countElement = document.getElementById('pageFeedCount');
  const ratingElement = document.getElementById('pageFeedRating');
  const ratingValueElement = document.getElementById('pageFeedRatingValue');
  if (!summary || !countElement || !ratingElement || !ratingValueElement) return;
  const errorElement = document.getElementById('pageFeedError');
  if (errorElement) {
    errorElement.textContent = currentPageSummaryError;
    errorElement.classList.toggle('hidden', !currentPageSummaryError);
  }

  const countKnown = Number.isInteger(currentPagePostCount) && currentPagePostCount >= 0;
  countElement.closest('.page-feed-count')?.classList.toggle('hidden', !countKnown);
  countElement.textContent = countKnown ? String(currentPagePostCount) : '';
  const hasRating = Number.isFinite(currentPageAverageRating) && currentPageAverageRating > 0;
  ratingElement.classList.toggle('hidden', !hasRating);
  ratingValueElement.textContent = hasRating
    ? String(Math.round(currentPageAverageRating * 10) / 10).replace(/\.0$/, '')
    : '';
}

function updateFeedHeader() {
  const headerHead = document.getElementById('feedHeaderHead');
  const heading = document.getElementById('feedHeading');
  const backButton = document.getElementById('backToMainFeed');
  const sortMenu = document.querySelector('.feed-sort-menu');
  if (!headerHead || !heading || !backButton) return;

  if (currentUrl) {
    if (sortMenu) sortMenu.hidden = false;
    document.body.classList.remove('homepage-layout', 'homepage-feed-expanded');
    headerHead.classList.add('is-page-view');
    heading.textContent = 'Page';
    heading.classList.remove('sr-only');
    backButton.classList.remove('hidden');
    updatePageFeedSummary();
    updateUrlPreviewBar(currentUrl, getPagePreviewTitle(currentUrl), getPagePreviewImage(), isPagePreviewVerified());
    return;
  }

  document.body.classList.toggle('homepage-layout', !currentChannel);
  if (sortMenu) sortMenu.hidden = true;
  closeSortMenu();
  if (currentChannel) document.body.classList.remove('homepage-feed-expanded');
  scheduleHomepageFeedPreviewState();
  headerHead.classList.remove('is-page-view');
  heading.textContent = 'Feed';
  heading.classList.remove('sr-only');
  backButton.classList.add('hidden');
  updatePageFeedSummary();
  updateUrlPreviewBar();
}

// Select a channel
async function selectChannel(channel) {
  displayFeedView();
  currentChannel = channel;
  currentUrl = null;
  resetFeedPagination();
  updateFeedHeader();
  window.scrollTo(0, 0);
  await loadFeed(true);
}

async function showPageFeed(pageUrl) {
  displayFeedView();
  currentChannel = null;
  currentUrl = pageUrl;
  resetFeedPagination();
  updateFeedHeader();
  window.scrollTo(0, 0);
  await loadFeed(true);
}

async function showPageView(pageUrl, pushHistory = true) {
  const normalizedUrl = String(pageUrl || '').trim();
  if (!normalizedUrl) return;
  if (pushHistory) {
    history.pushState({ type: 'page', pageUrl: normalizedUrl }, '', `/p/${encodeURIComponent(normalizedUrl)}`);
    return runRouteTransition(() => showPageView(normalizedUrl, false));
  }
  await showPageFeed(normalizedUrl);
}

// Load feed
async function loadFeed(reset = false) {
  if (!reset && (isLoading || !hasMore || messages.length === 0)) return;
  const routeGeneration = activeRouteGeneration;
  const loadGeneration = reset ? ++feedLoadGeneration : feedLoadGeneration;
  const isCurrent = () => routeGeneration === activeRouteGeneration && loadGeneration === feedLoadGeneration;
  const pageUrl = currentUrl;
  isLoading = true;
  if (reset) {
    resetFeedPagination();
    pagePostMetadataCache.clear();
    drainPageMetadataJobs();
  }
  const container = document.getElementById('messagesContainer');
  container.querySelector('.feed-load-error')?.remove();
  try {
    const params = new URLSearchParams({ limit: '20' });
    if (!reset) params.set('skip', String(messages.length));
    if (currentChannel) params.set('channel', currentChannel);
    if (pageUrl) params.set('url', pageUrl);
    if (userPreferences.verifiedOnly) params.set('verifiedOnly', 'true');
    if (userPreferences.minScore !== null) params.set('minScore', String(userPreferences.minScore));
    if (userPreferences.defaultSortingMethod) params.set('sortingMethod', userPreferences.defaultSortingMethod);
    const data = await apiFetchFeed(params);
    if (!isCurrent()) return;
    const returnedMessages = readFeedMessages(data);
    hasMore = returnedMessages.length > 0 && data.hasMore === true;
    const knownIds = new Set(messages.map(message => message.messageId));
    const newMessages = returnedMessages.filter(message => !knownIds.has(message.messageId));
    if (returnedMessages.length && !newMessages.length && hasMore) throw new Error('Feed pagination did not advance');
    if (!returnedMessages.length && !messages.length) {
      hasMore = false;
      container.innerHTML = '<div class="empty-state"><h3>No messages yet</h3><p>Be the first to post something!</p></div>';
      if (pageUrl) {
        currentPagePostCount = 0;
        currentPageAverageRating = 0;
        updatePageFeedSummary();
      }
      return;
    }
    await hydrateMessageMentions(newMessages);
    if (!isCurrent()) return;
    messages = reset ? newMessages : [...messages, ...newMessages];
    if (messages.length) lastMessageTimestamp = Math.max(...messages.map(message => message.timestamp || 0));
    if (pageUrl && !hasMore) {
      const ratings = messages.map(getMessageRating).filter(rating => rating > 0);
      const metadata = {
        total: messages.length,
        averageRating: ratings.length ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length : 0
      };
      pagePostMetadataCache.set(pageUrl, { promise: Promise.resolve(metadata), expiresAt: Date.now() + PAGE_POST_METADATA_TTL_MS });
    }
    if (reset) renderMessages();
    else renderMessages({ append: newMessages });
    void hydratePagePostMetadata(newMessages, { summaryPageUrl: pageUrl || '' });
  } catch (error) {
    if (!isCurrent()) return;
    console.error('Error loading feed:', error);
    container.querySelector('#loading-indicator')?.remove();
    if (!messages.length) hasMore = false;
    const errorHtml = `<div class="feed-load-error empty-state" role="alert"><p>${escapeHtml(error.message || 'Error loading feed')}</p><button type="button" class="btn btn-secondary" data-retry-feed>Retry</button></div>`;
    if (!messages.length) container.innerHTML = errorHtml;
    else container.insertAdjacentHTML('beforeend', errorHtml);
  } finally {
    if (isCurrent()) isLoading = false;
  }
}

// Append pagination results without replacing already rendered cards.
function renderMessages({ append = null } = {}) {
  const messagesContainer = document.getElementById('messagesContainer');
  updateFeedHeader();
  const html = (append ?? messages).map(msg => createMessageCard(msg, {
    isLoggedIn, currentUser,
    webPageView: !currentUrl,
    feedType: currentUrl ? 'page' : 'global',
    promoteFirstEmbeddedUrl: Boolean(currentUrl)
  })).join('');
  messagesContainer.querySelector('#loading-indicator')?.remove();
  if (append === null) messagesContainer.innerHTML = html;
  else messagesContainer.insertAdjacentHTML('beforeend', html);
  if (hasMore) {
    const loadingIndicator = document.createElement('div');
    loadingIndicator.id = 'loading-indicator';
    loadingIndicator.className = 'loading-indicator';
    loadingIndicator.innerHTML = '<p>Loading more...</p>';
    messagesContainer.appendChild(loadingIndicator);
  }
  scheduleHomepageFeedPreviewState();
}

// Setup intersection observer — delegates to shared/js/feed.js
// (called in DOMContentLoaded above with setupIntersectionObserver(() => loadFeed(false), 'loading-indicator'))

// Fetch only new messages
async function fetchNewMessages() {
  if(!lastMessageTimestamp || isLoading) return;
  const routeGeneration = activeRouteGeneration;
  const loadGeneration = feedLoadGeneration;
  const isCurrent = () => routeGeneration === activeRouteGeneration && loadGeneration === feedLoadGeneration;
  
  try {
    const params = new URLSearchParams();
    params.append('since', lastMessageTimestamp.toString());
    
    if(currentChannel) params.append('channel', currentChannel);
    if(currentUrl) params.append('url', currentUrl);
    if(userPreferences.verifiedOnly) params.append('verifiedOnly', 'true');
    if(userPreferences.minScore !== 0) params.append('minScore', userPreferences.minScore.toString());
    if(userPreferences.defaultSortingMethod) params.append('sortingMethod', userPreferences.defaultSortingMethod);
    
    const data = await apiFetchFeed(params);
    if (!isCurrent()) return;
    const knownIds = new Set(messages.map(message => message.messageId));
    const newMessages = readFeedMessages(data).filter(message => !knownIds.has(message.messageId));
    
    if(newMessages.length > 0) {
      await hydrateMessageMentions(newMessages);
      if (!isCurrent()) return;
      messages = [...newMessages, ...messages];
      lastMessageTimestamp = Math.max(...messages.map(m => m.timestamp || 0));
      renderMessages();
      void hydratePagePostMetadata(newMessages, { summaryPageUrl: currentUrl || '' });
    }
  } catch (error) {
    console.error('Error fetching new messages:', error);
  }
}

// ---------------------------------------------------------------------------
// Thread view — single message + replies
// ---------------------------------------------------------------------------

async function showThreadView(messageId, pushHistory = true) {
  if (pushHistory) {
    history.pushState({ type: 'thread', messageId }, '', `/m/${encodeURIComponent(messageId)}`);
    return runRouteTransition(() => showThreadView(messageId, false));
  }

  deactivateStandalonePages();
  isThreadView = true;
  const routeGeneration = activeRouteGeneration;
  currentThreadMessageId = messageId;
  document.body.classList.remove('homepage-layout', 'homepage-feed-expanded');

  // Show thread container, hide feed
  const feedEl   = document.querySelector('.feed');
  const threadEl = document.getElementById('threadView');
  const profileViewEl = document.getElementById('profileView');
  const searchViewEl = document.getElementById('searchView');
  if (feedEl)   feedEl.style.display = 'none';
  if (threadEl) threadEl.style.display = 'block';
  if (profileViewEl) profileViewEl.style.display = 'none';
  if (searchViewEl) searchViewEl.style.display = 'none';
  window.scrollTo(0, 0);

  const knownMessage = messages.find(message => String(message?.messageId || '') === String(messageId));
  const knownPageUrl = knownMessage?.canonicalUrl || knownMessage?.url || '';
  currentPagePostCount = null;
  currentPageAverageRating = null;
  currentPageSummaryError = '';
  updateUrlPreviewBar(
    knownPageUrl,
    knownPageUrl ? getPagePreviewTitle(knownPageUrl, knownMessage) : '',
    getPagePreviewImage(knownMessage),
    isPagePreviewVerified(knownMessage)
  );
  updatePageFeedSummary();

  const threadContent = document.getElementById('threadContent');
  if (threadContent) threadContent.innerHTML = '<p class="loading">Loading...</p>';
  const threadReplies = document.getElementById('threadReplies');
  if (threadReplies) threadReplies.innerHTML = '';

  try {
    const data = await apiFetchMessage(messageId);
    if (routeGeneration !== activeRouteGeneration || currentThreadMessageId !== messageId) return;
    const backToThreadBtn = document.getElementById('backToThread');
    if(data.message?.depth > 0) {
      backToThreadBtn?.classList.remove('hidden');

      // Add data-parent-id attribute to the backToThread button for navigation
      if (backToThreadBtn) {
        backToThreadBtn.setAttribute('data-parent-id', data.message.inReplyTo || '');
      }
    } else {
      backToThreadBtn?.classList.add('hidden');
    }
    const replies = data.replies || [];
    await hydrateMessageMentions([data.message, ...replies]);
    if (routeGeneration !== activeRouteGeneration || !isThreadView || currentThreadMessageId !== messageId) return;
    renderThread(data.message, replies);
    void hydrateThreadPageSummary(data.message);
  } catch (err) {
    if (routeGeneration !== activeRouteGeneration || currentThreadMessageId !== messageId) return;
    console.error('Error loading thread:', err);
    if (threadContent) threadContent.innerHTML = `<div class="empty-state"><h3>Unable to load post</h3><p>${escapeHtml(err.message || 'Please try again later')}</p></div>`;
  }
}

async function showFeedView() {
  displayFeedView();
  currentChannel = null;
  currentUrl = null;
  resetFeedPagination();
  updateFeedHeader();
  window.scrollTo(0, 0);
  await loadFeed(true);
}

async function hydrateThreadPageSummary(message) {
  const pageUrl = getMessagePageUrl(message);
  if (!pageUrl) return;
  const routeGeneration = activeRouteGeneration;
  const expectedThread = currentThreadMessageId;
  try {
    const metadata = await fetchPagePostMetadata(pageUrl);
    if (!metadata || routeGeneration !== activeRouteGeneration || !isThreadView || currentThreadMessageId !== expectedThread) return;
    currentPagePostCount = metadata.total;
    currentPageAverageRating = metadata.averageRating;
    currentPageSummaryError = '';
    updatePageFeedSummary();
  } catch (error) {
    if (routeGeneration !== activeRouteGeneration) return;
    console.warn('Could not hydrate page activity:', error);
    currentPageSummaryError = 'Page activity unavailable';
    updatePageFeedSummary();
  }
}

async function resolvePublicProfile(identifier) {
  const requestedIdentifier = String(identifier || '').trim();
  if (!requestedIdentifier) return null;

  const directResponse = await fetch(`${API_BASE_URL}/user/profile/${encodeURIComponent(requestedIdentifier)}`, { credentials: 'include' });
  if (directResponse.ok) {
    const profile = await directResponse.json();
    const pubkey = String(profile.pubkey || '').trim();
    if (!pubkey) throw new Error('Profile response is missing its public key');
    return { pubkey, profile };
  }
  if (directResponse.status !== 404) throw new Error('Profile load failed');

  const assignedName = getProfileSearchQuery(requestedIdentifier);
  if (assignedName === requestedIdentifier) return null;
  const exactQuery = `^${assignedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`;
  const searchData = await apiSearch(exactQuery);
  const requestedSlug = requestedIdentifier.toLocaleLowerCase();
  const matches = (Array.isArray(searchData.users) ? searchData.users : []).filter(candidate => (
    getProfileUrlSlug(candidate).toLocaleLowerCase() === requestedSlug
    || getAssignedProfileUrlSlug(candidate).toLocaleLowerCase() === requestedSlug
  ));
  if (matches.length > 1) throw new Error('Profile identifier is ambiguous');
  const pubkey = getPersonPubkey(matches[0]);
  if (!pubkey) return null;

  const profileResponse = await fetch(`${API_BASE_URL}/user/profile/${encodeURIComponent(pubkey)}`, { credentials: 'include' });
  if (profileResponse.status === 404) return null;
  if (!profileResponse.ok) throw new Error('Profile load failed');
  return { pubkey, profile: await profileResponse.json() };
}

async function showProfileView(profileIdentifier, pushHistory = true) {
  const requestedIdentifier = String(profileIdentifier || '').trim();
  if (!requestedIdentifier) return;
  if (pushHistory) {
    history.pushState({ type: 'profile', profileIdentifier: requestedIdentifier }, '', `/u/${encodeURIComponent(requestedIdentifier)}`);
    return runRouteTransition(() => showProfileView(requestedIdentifier, false));
  }

  deactivateStandalonePages();
  currentUserProfile = requestedIdentifier;
  const routeGeneration = activeRouteGeneration;
  const routeSuffix = window.location.pathname.match(/\/(details|followers|following)$/)?.[0] || '';
  currentProfileViewState = null;
  currentProfileRelationshipState = null;
  document.body.classList.remove('homepage-layout', 'homepage-feed-expanded');

  const feedEl = document.querySelector('.feed');
  const threadEl = document.getElementById('threadView');
  const profileViewEl = document.getElementById('profileView');
  const searchViewEl = document.getElementById('searchView');
  if (feedEl) feedEl.style.display = 'none';
  if (threadEl) threadEl.style.display = 'none';
  if (profileViewEl) profileViewEl.style.display = 'block';
  if (searchViewEl) searchViewEl.style.display = 'none';
  setProfileDetailsVisible(false);
  setProfileRelationshipsVisible(false);
  updateUrlPreviewBar();
  window.scrollTo(0, 0);

  const profileContent = document.getElementById('profileContent');
  if (profileContent) profileContent.innerHTML = '<p class="loading">Loading profile…</p>';

  try {
    const resolvedProfile = await resolvePublicProfile(requestedIdentifier);
    if (routeGeneration !== activeRouteGeneration) return;
    if (!resolvedProfile) {
      if (profileContent) profileContent.innerHTML = '<div class="empty-state"><h3>User not found</h3></div>';
      return;
    }

    const { pubkey, profile } = resolvedProfile;
    currentUserProfile = pubkey;
    const messagesRes = await fetch(`${API_BASE_URL}/user/messages/${encodeURIComponent(pubkey)}`, { credentials: 'include' });
    if (routeGeneration !== activeRouteGeneration) return;
    if (!messagesRes.ok) throw new Error(`Profile posts could not be loaded (HTTP ${messagesRes.status})`);
    const messagesData = await messagesRes.json();
    if (routeGeneration !== activeRouteGeneration) return;
    const canonicalPath = getProfilePath(profile, pubkey);
    const userMessages = readFeedMessages(messagesData);
    await hydrateMessageMentions(userMessages);
    if (routeGeneration !== activeRouteGeneration || currentUserProfile !== pubkey) return;
    const routeType = routeSuffix === '/details' ? 'profile-details' : routeSuffix ? 'profile-relationships' : 'profile';
    history.replaceState({ ...history.state, type: routeType, pubkey }, '', `${canonicalPath}${routeSuffix}`);
    currentProfileViewState = {
      pubkey,
      profile,
      canonicalPath,
      userMessages,
      activeTab: 'posts'
    };
    renderProfileView();
    void hydratePagePostMetadata(userMessages);
  } catch (err) {
    if (routeGeneration !== activeRouteGeneration) return;
    console.error('Error loading profile view:', err);
    if (profileContent) profileContent.innerHTML = `<div class="empty-state"><h3>Error loading profile</h3><p>${escapeHtml(err.message || 'Please try again later')}</p></div>`;
  }
}

function renderProfileView() {
  const state = currentProfileViewState;
  if (!state) return;
  setProfileDetailsVisible(false);
  setProfileRelationshipsVisible(false);
  const { pubkey, profile } = state;
  const identity = getProfileIdentity(profile);
  const heading = document.getElementById('profileHeading');
  const secondary = document.getElementById('profileNavSecondary');
  const avatar = document.getElementById('profileNavAvatar');
  const stats = document.getElementById('profileRelationshipStats');
  const actions = document.getElementById('profileNavbarActions');
  if (heading) heading.innerHTML = `${escapeHtml(identity.primary)}${identity.verified ? '<i class="fa-solid fa-badge-check verified-account-badge" aria-label="Verified username"></i>' : ''}`;
  if (secondary) secondary.textContent = identity.secondary;
  if (avatar) avatar.innerHTML = `${escapeHtml(identity.initial)}${identity.avatarUrl ? `<img src="${escapeHtml(identity.avatarUrl)}" alt="">` : ''}`;
  if (stats) stats.innerHTML = `<button class="profile-relationship-stat" type="button" data-open-relationships="following"><strong>${escapeHtml(String(profile.followingCount ?? 0))}</strong><span>Following</span></button><button class="profile-relationship-stat" type="button" data-open-relationships="followers"><strong>${escapeHtml(String(profile.followersCount ?? 0))}</strong><span>Followers</span></button>`;

  if (actions) {
    const isOwnProfile = currentUser && currentUser.pubkey === pubkey;
    const isFollowing = isUserFollowing(pubkey);
    actions.innerHTML = `
      <button class="profile-navbar-action" type="button" data-profile-details>View profile</button>
      <button class="profile-navbar-action" type="button" data-share-profile>Share profile</button>
      ${isLoggedIn && !isOwnProfile ? `<button class="profile-navbar-action primary follow-btn" type="button" data-follow-pubkey="${escapeHtml(pubkey)}" data-following="${isFollowing}">${isFollowing ? 'Following' : 'Follow'}</button>` : ''}`;
  }
  renderProfileTab(state.activeTab);
}

function renderProfileTab(tab = 'posts') {
  const state = currentProfileViewState;
  const content = document.getElementById('profileContent');
  if (!state || !content) return;
  const activeTab = ['posts', 'spots', 'usernames'].includes(tab) ? tab : 'posts';
  state.activeTab = activeTab;
  document.querySelectorAll('[data-profile-tab]').forEach(button => {
    const active = button.dataset.profileTab === activeTab;
    button.classList.toggle('active', active);
    button.setAttribute('aria-selected', String(active));
  });

  if (activeTab === 'posts') {
    content.innerHTML = state.userMessages.length
      ? `<div id="profileUserMessages">${state.userMessages.map(message => createMessageCard(message, { isLoggedIn, currentUser, webPageView: true, authorProfile: state.profile })).join('')}</div>`
      : '<div class="empty-state">No posts yet.</div>';
    return;
  }
  if (activeTab === 'spots') {
    content.innerHTML = '<div class="empty-state">Spots are not publicly available for this user.</div>';
    return;
  }
  if (activeTab === 'usernames') {
    const identity = getProfileIdentity(state.profile);
    const domains = Array.isArray(state.profile.verifiedDomains) ? state.profile.verifiedDomains : [];
    const rows = [
      identity.verified ? `<div class="profile-info-item"><label>Username</label><div class="value">${escapeHtml(identity.primary)} <i class="fa-solid fa-badge-check verified-account-badge" aria-label="Verified username"></i></div></div>` : '',
      domains.length ? `<div class="profile-info-item"><label>Verified domains</label><div class="value">${domains.map(domain => escapeHtml(typeof domain === 'string' ? domain : domain?.domain || '')).filter(Boolean).join(', ')}</div></div>` : ''
    ].filter(Boolean).join('');
    content.innerHTML = rows ? `<div class="profile-detail-list">${rows}</div>` : '<div class="empty-state">This user does not have a username yet.</div>';
    return;
  }

  const profile = state.profile;
  content.innerHTML = `<div class="profile-detail-list">
    <div class="profile-info-item"><label>Reputation</label><div class="value">${escapeHtml(String(profile.reputation ?? 0))}</div></div>
    <div class="profile-info-item"><label>Platform reputation</label><div class="value">${escapeHtml(String(profile.platformReputation ?? 0))}</div></div>
    <div class="profile-info-item"><label>Public key</label><div class="value profile-pubkey">${escapeHtml(state.pubkey)}</div></div>
  </div>`;
}

function relationshipEntries(value, key) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];
  for (const candidate of [key, 'users', 'people', 'accounts', 'results']) {
    if (Array.isArray(value[candidate])) return value[candidate];
  }
  return relationshipEntries(value.data, key);
}

function getRelationshipPubkey(person) {
  if (typeof person === 'string') return person.trim();
  if (!person || typeof person !== 'object') return '';
  return getPersonPubkey(person)
    || getPersonPubkey(person.user)
    || getPersonPubkey(person.profile)
    || String(person.followedPubkey || person.followerPubkey || '').trim();
}

function normalizeRelationshipPerson(entry) {
  const base = entry && typeof entry === 'object' ? entry : {};
  const nestedUser = base.user && typeof base.user === 'object' ? base.user : {};
  const nestedProfile = base.profile && typeof base.profile === 'object' ? base.profile : {};
  return {
    ...base,
    ...nestedUser,
    ...nestedProfile,
    pubkey: getRelationshipPubkey(entry)
  };
}

async function hydrateRelationshipPerson(entry) {
  const localPerson = normalizeRelationshipPerson(entry);
  if (!localPerson.pubkey) return null;
  if (currentUser?.pubkey === localPerson.pubkey) return { ...localPerson, ...currentUser, pubkey: localPerson.pubkey };

  try {
    const response = await fetch(`${API_BASE_URL}/user/profile/${encodeURIComponent(localPerson.pubkey)}`, { credentials: 'include' });
    if (!response.ok) return localPerson;
    const profile = await response.json();
    return { ...localPerson, ...profile, pubkey: localPerson.pubkey };
  } catch (error) {
    console.warn('Unable to load relationship profile:', error);
    return localPerson;
  }
}

async function hydrateRelationshipPeople(entries) {
  const seen = new Set();
  const uniqueEntries = (Array.isArray(entries) ? entries : []).filter(entry => {
    const pubkey = getRelationshipPubkey(entry);
    if (!pubkey || seen.has(pubkey)) return false;
    seen.add(pubkey);
    return true;
  });
  return (await Promise.all(uniqueEntries.map(hydrateRelationshipPerson))).filter(Boolean);
}

function relationshipPersonRowHtml(person) {
  const pubkey = getRelationshipPubkey(person);
  const identity = getProfileIdentity(person);
  const isOwnProfile = currentUser?.pubkey === pubkey;
  const isFollowing = isUserFollowing(pubkey);
  return `<div class="people-row" data-people-pubkey="${escapeHtml(pubkey)}">
    <button class="people-identity" type="button" data-relationship-profile="${escapeHtml(pubkey)}" aria-label="Open ${escapeHtml(identity.primary)} profile">
      <span class="people-avatar" aria-hidden="true">${escapeHtml(identity.initial)}${identity.avatarUrl ? `<img src="${escapeHtml(identity.avatarUrl)}" alt="">` : ''}</span>
      <span class="people-info">
        <span class="people-name">${escapeHtml(identity.primary)}${identity.verified ? '<i class="fa-solid fa-badge-check verified-account-badge" aria-label="Verified username"></i>' : ''}</span>
        ${identity.secondary ? `<span class="people-username">${escapeHtml(identity.secondary)}</span>` : ''}
      </span>
    </button>
    ${isLoggedIn && !isOwnProfile ? `<button class="people-follow-button follow-btn" type="button" data-follow-pubkey="${escapeHtml(pubkey)}" data-following="${isFollowing}">${isFollowing ? 'Following' : 'Follow'}</button>` : ''}
  </div>`;
}

function setProfileRelationshipsVisible(visible) {
  document.getElementById('profileOverview')?.classList.toggle('hidden', visible);
  document.getElementById('profileDetailsView')?.classList.add('hidden');
  document.getElementById('profileRelationshipsView')?.classList.toggle('hidden', !visible);
}

function renderProfileRelationshipTab(tab = 'following') {
  const state = currentProfileRelationshipState;
  const content = document.getElementById('profileRelationshipsContent');
  if (!state || !content) return;
  const activeTab = tab === 'followers' ? 'followers' : 'following';
  state.activeTab = activeTab;
  document.querySelectorAll('[data-relationship-tab]').forEach(button => {
    const active = button.dataset.relationshipTab === activeTab;
    button.classList.toggle('active', active);
    button.setAttribute('aria-selected', String(active));
  });

  const people = state[activeTab] || [];
  if (!people.length) {
    content.innerHTML = `<div class="empty-state">${activeTab === 'following' ? "This user isn't following anyone yet." : 'This user has no followers yet.'}</div>`;
    return;
  }
  content.innerHTML = `<div class="people-rows">${people.map(relationshipPersonRowHtml).join('')}</div>`;
}

function selectProfileRelationshipTab(tab) {
  const state = currentProfileRelationshipState;
  if (!state) return;
  const activeTab = tab === 'followers' ? 'followers' : 'following';
  renderProfileRelationshipTab(activeTab);
  history.replaceState(
    { ...history.state, type: 'profile-relationships', pubkey: state.pubkey, relationshipTab: activeTab },
    '',
    `${state.canonicalPath}/${activeTab}`
  );
}

async function showProfileRelationships(tab = 'following', pushHistory = true) {
  const profileState = currentProfileViewState;
  if (!profileState) return;
  const activeTab = tab === 'followers' ? 'followers' : 'following';
  const relationshipPath = `${profileState.canonicalPath}/${activeTab}`;
  if (pushHistory) {
    history.pushState(
      { type: 'profile-relationships', pubkey: profileState.pubkey, relationshipTab: activeTab, relationshipFromProfile: true },
      '',
      relationshipPath
    );
    return runRouteTransition(() => showProfileRelationships(activeTab, false));
  }

  setProfileRelationshipsVisible(true);
  const routeGeneration = activeRouteGeneration;
  window.scrollTo(0, 0);
  const backLink = document.querySelector('[data-profile-relationships-back]');
  if (backLink) backLink.href = profileState.canonicalPath;
  const content = document.getElementById('profileRelationshipsContent');
  if (content) content.innerHTML = '<p class="loading">Loading people…</p>';
  currentProfileRelationshipState = {
    pubkey: profileState.pubkey,
    canonicalPath: profileState.canonicalPath,
    following: [],
    followers: [],
    activeTab
  };
  history.replaceState(
    { ...history.state, type: 'profile-relationships', pubkey: profileState.pubkey, relationshipTab: activeTab },
    '',
    relationshipPath
  );

  try {
    const [followingResponse, followersResponse] = await Promise.all([
      fetch(`${API_BASE_URL}/user/following/${encodeURIComponent(profileState.pubkey)}`, { credentials: 'include' }),
      fetch(`${API_BASE_URL}/user/followers/${encodeURIComponent(profileState.pubkey)}`, { credentials: 'include' })
    ]);
    if (routeGeneration !== activeRouteGeneration) return;
    if (!followingResponse.ok || !followersResponse.ok) throw new Error('Relationship list unavailable');
    const [followingData, followersData] = await Promise.all([followingResponse.json(), followersResponse.json()]);
    const [following, followers] = await Promise.all([
      hydrateRelationshipPeople(relationshipEntries(followingData, 'following')),
      hydrateRelationshipPeople(relationshipEntries(followersData, 'followers'))
    ]);
    if (routeGeneration !== activeRouteGeneration || !currentProfileRelationshipState || currentProfileRelationshipState.pubkey !== profileState.pubkey) return;
    currentProfileRelationshipState.following = following;
    currentProfileRelationshipState.followers = followers;
    renderProfileRelationshipTab(activeTab);
  } catch (error) {
    if (routeGeneration !== activeRouteGeneration) return;
    console.error('Error loading relationships:', error);
    if (content) content.innerHTML = '<div class="empty-state">Unable to load people.</div>';
  }
}

function abbreviateProfileValue(value, startLength = 8, endLength = 8) {
  const text = String(value || '').trim();
  if (text.length <= startLength + endLength + 3) return text;
  return `${text.slice(0, startLength)}...${text.slice(-endLength)}`;
}

function getPublicWalletAddress(profile = {}) {
  return String(profile.bitcoinAddress || profile.ordinalsAddress || profile.walletAddress || '').trim();
}

function setProfileDetailsVisible(visible) {
  document.getElementById('profileOverview')?.classList.toggle('hidden', visible);
  document.getElementById('profileDetailsView')?.classList.toggle('hidden', !visible);
}

function renderProfileDetails() {
  const state = currentProfileViewState;
  if (!state) return;
  const { profile, pubkey } = state;
  const identity = getProfileIdentity(profile);
  const avatar = document.getElementById('profileDetailsNavAvatar');
  const heading = document.getElementById('profileDetailsHeading');
  const secondary = document.getElementById('profileDetailsSecondary');
  const content = document.getElementById('profileDetailsContent');
  if (avatar) avatar.innerHTML = `${escapeHtml(identity.initial)}${identity.avatarUrl ? `<img src="${escapeHtml(identity.avatarUrl)}" alt="">` : ''}`;
  if (heading) heading.innerHTML = `${escapeHtml(identity.primary)}${identity.verified ? '<i class="fa-solid fa-badge-check verified-account-badge" aria-label="Verified username"></i>' : ''}`;
  if (secondary) secondary.textContent = identity.secondary;
  if (!content) return;

  const domains = (Array.isArray(profile.verifiedDomains) ? profile.verifiedDomains : [])
    .map(domain => String(typeof domain === 'string' ? domain : domain?.domain || '').trim())
    .filter(Boolean)
    .join(', ');
  const username = String(profile.userName || profile.username || profile.fullName || profile.displayName || '').trim();
  const walletAddress = getPublicWalletAddress(profile);
  content.innerHTML = `<div class="profile-details-section">
    <div class="profile-details-row"><span class="profile-details-label">Username</span><span class="profile-details-value">${escapeHtml(username || 'Not set')}</span></div>
    ${domains ? `<div class="profile-details-row"><span class="profile-details-label">Domains</span><span class="profile-details-value">${escapeHtml(domains)}</span></div>` : ''}
    <div class="profile-details-row"><span class="profile-details-label">Reputation</span><span class="profile-details-value">${escapeHtml(String(profile.platformReputation ?? 0))}</span></div>
    <div class="profile-details-row"><span class="profile-details-label">Ordinals wallet</span><span class="profile-details-value">${escapeHtml(walletAddress ? abbreviateProfileValue(walletAddress, 2, 8) : 'Not linked')}</span></div>
    <button class="profile-details-row profile-details-copy-row" type="button" data-copy-profile-pubkey="${escapeHtml(pubkey)}"><span class="profile-details-label">Public key</span><span class="profile-details-copy-action"><span class="profile-details-value">${escapeHtml(abbreviateProfileValue(pubkey))}</span><i class="fa-light fa-copy" aria-hidden="true"></i></span></button>
  </div>`;
}

async function showProfileDetails(pushHistory = true) {
  const state = currentProfileViewState;
  if (!state) return;
  if (pushHistory) {
    history.pushState({ type: 'profile-details', pubkey: state.pubkey, profileParent: true }, '', `${state.canonicalPath}/details`);
    return runRouteTransition(() => showProfileDetails(false), { showLoader: false });
  }
  renderProfileDetails();
  setProfileDetailsVisible(true);
  window.scrollTo(0, 0);
}

function closeProfileDetails() {
  const state = currentProfileViewState;
  if (!state) return;
  if (history.state?.profileParent) {
    history.back();
    return;
  }
  setProfileDetailsVisible(false);
  history.replaceState({ type: 'profile', pubkey: state.pubkey }, '', state.canonicalPath);
  window.scrollTo(0, 0);
}

async function shareCurrentProfile() {
  const state = currentProfileViewState;
  if (!state) return;
  const identity = getProfileIdentity(state.profile);
  const url = `${window.location.origin}${state.canonicalPath || getProfilePath(state.profile, state.pubkey)}`;
  try {
    if (navigator.share) await navigator.share({ title: `${identity.primary} on Kekko`, url });
    else {
      await navigator.clipboard.writeText(url);
      showToast('Profile link copied');
    }
  } catch (error) {
    if (error?.name !== 'AbortError') console.error('Profile sharing failed:', error);
  }
}

async function showChannelView(channelDomain, pushHistory = true) {
  if (pushHistory) {
    if (channelDomain !== null) {
      history.pushState({ type: 'channel', channelDomain }, '', `/c/${encodeURIComponent(channelDomain)}`);
    } else {
      history.pushState({ type: 'feed' }, '', '/');
    }
    return runRouteTransition(() => showChannelView(channelDomain, false));
  }

  await selectChannel(channelDomain);
}

function renderThread(message, replies) {
  const threadContent = document.getElementById('threadContent');
  const threadReplies = document.getElementById('threadReplies');
  if (!threadContent || !threadReplies) return;

  const pageUrl = message?.canonicalUrl || message?.url || '';
  updateUrlPreviewBar(
    pageUrl,
    pageUrl ? getPagePreviewTitle(pageUrl, message) : '',
    getPagePreviewImage(message),
    isPagePreviewVerified(message)
  );

  threadContent.innerHTML = createMessageCard(message, { isLoggedIn, currentUser, inThread: true });

  if (replies.length === 0) {
    threadReplies.innerHTML = '<p class="empty-message" style="padding:1rem;color:var(--text-secondary);">No replies yet.</p>';
  } else {
    threadReplies.innerHTML = replies.map(r => createMessageCard(r, { isLoggedIn, currentUser, inThread: true })).join('');
  }
}

// Poll for new replies while a thread is open
async function fetchNewThreadReplies() {
  if (!isThreadView || !currentThreadMessageId) return;
  const expectedThread = currentThreadMessageId;
  const routeGeneration = activeRouteGeneration;
  try {
    const data = await apiFetchMessage(expectedThread);
    const replies = data.replies || [];
    await hydrateMessageMentions([data.message, ...replies]);
    if (routeGeneration !== activeRouteGeneration || !isThreadView || currentThreadMessageId !== expectedThread) return;
    renderThread(data.message, replies);
  } catch (err) {
    console.error('Error refreshing thread:', err);
  }
}

// Toggle profile dropdown
function toggleProfileDropdown() {
  const dropdown = document.getElementById('profileDropdown');
  dropdown.classList.toggle('show');
}

// Close dropdown when clicking outside
window.addEventListener('click', function(event) {
  if(!event.target.matches('.profile-dropdown-btn')) {
    const dropdowns = document.getElementsByClassName('profile-dropdown');
    for(let i = 0; i < dropdowns.length; i++) {
      dropdowns[i].classList.remove('show');
    }
  }
});

// Show preferences modal
function showPreferencesModal() {
  // Load current preferences
  document.getElementById('verifiedOnly').checked = userPreferences.verifiedOnly;
  document.getElementById('minScore').value = userPreferences.minScore;
  document.getElementById('minScoreValue').textContent = userPreferences.minScore;
  
  document.getElementById('preferencesModal').classList.add('show');
}

// Update score value display
function updateScoreValue() {
  const scoreValue = document.getElementById('minScore').value;
  document.getElementById('minScoreValue').textContent = scoreValue;
}

// Handle preferences form submission
async function handlePreferences(event) {
  event.preventDefault();
  
  const verifiedOnly = document.getElementById('verifiedOnly').checked;
  const minScore = parseInt(document.getElementById('minScore').value);
  const errorDiv = document.getElementById('preferencesError');
  const successDiv = document.getElementById('preferencesSuccess');
  const sortingMethodSelect = document.getElementById('sortingMethod');
  const sortingMethod = sortingMethodSelect ? sortingMethodSelect.value : 'new';
  
  try {
    await apiSavePreferences(verifiedOnly, minScore, sortingMethod);
    userPreferences.verifiedOnly = verifiedOnly;
    userPreferences.minScore = minScore;
    userPreferences.defaultSortingMethod = sortingMethod;
    hideError(errorDiv);
    showSuccess(successDiv, 'Preferences saved successfully!');
    setTimeout(() => { closeModal('preferencesModal'); loadFeed(true); }, 1500);
  } catch (error) {
    console.error('Error saving preferences:', error);
    hideSuccess(successDiv);
    showError(errorDiv, error.message || 'Failed to save preferences');
  }
}

// Handle login
async function handleLogin(event) {
  event.preventDefault();
  
  const email = document.getElementById('loginEmail').value;
  const password = document.getElementById('loginPassword').value;
  const errorDiv = document.getElementById('loginError');
  
  try {
    await apiLogin(email, password);
    closeModal('loginModal');
    isLoggedIn = true;
    //await liteClient.startUp();
    await loadProfile();
    updateNavigation();
    loadFeed(true);
    document.getElementById('loginForm').reset();
  } catch (error) {
    console.error('Error during login:', error);
    showError(errorDiv, error.message || 'An error occurred during login');
  }
}

// Handle registration
async function handleRegister(event) {
  event.preventDefault();
  
  const email = document.getElementById('registerEmail').value;
  const displayName = document.getElementById('registerDisplayName').value;
  const fullName = document.getElementById('registerFullName').value;
  const password = document.getElementById('registerPassword').value;
  const errorDiv = document.getElementById('registerError');
  const successDiv = document.getElementById('registerSuccess');
  
  try {
    await apiRegister(email, displayName, fullName, password);
    hideError(errorDiv);
    showSuccess(successDiv, 'Registration successful! Please check your email for activation code.');
    document.getElementById('registerForm').reset();
    setTimeout(() => { closeModal('registerModal'); showActivationModal(); }, 2000);
  } catch (error) {
    console.error('Error during registration:', error);
    hideSuccess(successDiv);
    showError(errorDiv, error.message || 'An error occurred during registration');
  }
}

/*
// Handle linked account creation
async function handleLinkedAccountCreation() {
  let mnemonicInput = document.getElementById("restoreAccountRecoveryPhrase").value;
  const localPassword = document.getElementById('unlockCreateAccountPassword').value;
  const accountType = "password";

  if(!mnemonicInput || mnemonicInput == "") mnemonicInput = false;
  const newAccountData = await createAccount(localPassword, accountType, mnemonicInput);
  
  document.getElementById("createAccountStatus").classList.remove("hidden");
  if(newAccountData) {
    document.getElementById("createAccountDiv").classList.add("hidden");

    if(mnemonicInput) {
      document.getElementById("createAccountStatus").innerHTML = `Account restored. Linking to your profile...`;
    } else {
      document.getElementById("createAccountStatus").innerHTML = `This is your recovery phrase for your private key and it will only be shown once. Write it down and store it in a safe place!<br><br>${newAccountData.mnemonic}<br><br>Linking to your profile...`;
    }
    
    // Now link the pubkey to the user account
    await linkPubkeyToAccount(newAccountData.publicKey);
  } else {
    document.getElementById("createAccountStatus").innerHTML = `There was an error creating/restoring your account. Please try again.`;
  }
}
*/

// Link public key to user account
/*
async function linkPubkeyToAccount(pubkey) {
  const statusEl = document.getElementById('createAccountStatus');
  try {
    const initData = await apiLinkPubkeyInit(pubkey);
    
    statusEl.innerHTML = 'Signing message to verify ownership...';
    let signature;
    try {
      signature = await liteClient.signMessage(initData.message);
    } catch(signError) {
      console.error('Error signing message:', signError);
      statusEl.innerHTML = 'Error signing message. Please try again.';
      return;
    }

    statusEl.innerHTML = 'Verifying signature...';
    await apiLinkPubkeyVerify(signature);

    statusEl.innerHTML = `Success! Your account has been linked.<br><br>Public key: ${pubkey}`;
    document.getElementById('unlockAccountDiv').classList.add('hidden');

    setTimeout(async () => {
      await loadProfile();
      closeModal('linkAccountModal');
      if(liteClient.account) {
        liteClient.account.mnemonic = null;
        liteClient.account.seed = null;
      }
    }, 2000);
  } catch(error) {
    console.error('Error linking pubkey:', error);
    statusEl.innerHTML = `Error: ${escapeHtml(error.message || 'An error occurred while linking your account. Please try again.')}`;
  }
}
*/

// Handle linked account unlock
/*
async function handleLinkedAccountUnlock() {
  const unlockAccountPassword = document.getElementById("unlockAccountPassword").value;
  if(await unlockAccount(unlockAccountPassword)) {
    
    // Now link the pubkey to the user account
    await linkPubkeyToAccount(liteClient.account.publicKey);
  } else {
    document.getElementById("createAccountStatus").innerHTML = `There was an error unlocking your account. Please try again.`;
  }
}

// Handle activation
async function handleActivation(event) {
  event.preventDefault();
  
  const email = document.getElementById('activationEmail').value;
  const activationCode = document.getElementById('activationCode').value;
  const password = document.getElementById('activationPassword').value;
  const errorDiv = document.getElementById('activationError');
  const successDiv = document.getElementById('activationSuccess');
  
  try {
    await apiActivate(email, activationCode, password);
    hideError(errorDiv);
    showSuccess(successDiv, 'Account activated! You can now login.');
    document.getElementById('activationForm').reset();
    setTimeout(() => { closeModal('activationModal'); showLoginModal(); }, 2000);
  } catch (error) {
    console.error('Error during activation:', error);
    hideSuccess(successDiv);
    showError(errorDiv, error.message || 'An error occurred during activation');
  }
}
*/

async function handleActivation(event) {
  event.preventDefault();
  const email = document.getElementById('activationEmail').value;
  const activationCode = document.getElementById('activationCode').value;
  const password = document.getElementById('activationPassword').value;
  const errorDiv = document.getElementById('activationError');
  const successDiv = document.getElementById('activationSuccess');

  try {
    await apiActivate(email, activationCode, password);
    hideError(errorDiv);
    showSuccess(successDiv, 'Account activated. You can now log in.');
    document.getElementById('activationForm').reset();
    setTimeout(() => { closeModal('activationModal'); showLoginModal(); }, 1200);
  } catch (error) {
    hideSuccess(successDiv);
    showError(errorDiv, error.message || 'Activation failed');
  }
}

// Handle logout
async function handleLogout() {
  try {
    await apiLogout();
    isLoggedIn = false;
    currentUser = null;
    document.getElementById('profileSidebar').style.display = 'none';
    updateNavigation();
    loadFeed(true);
  } catch (error) {
    console.error('Error during logout:', error);
  }
}

// Handle follow / unfollow
function isUserFollowing(pubkey) {
  const following = Array.isArray(currentUser?.following) ? currentUser.following : [];
  return following.some(person => getRelationshipPubkey(person) === pubkey);
}

async function handleFollowUser(pubkey) {
  if (!isLoggedIn) return;
  const isFollowing = isUserFollowing(pubkey);
  try {
    if (isFollowing) {
      await apiUnfollow(pubkey);
      if (currentUser.following) {
        currentUser.following = currentUser.following.filter(person => getRelationshipPubkey(person) !== pubkey);
      }
    } else {
      await apiFollow(pubkey);
      if (currentUser.following) {
        currentUser.following.push(pubkey);
      } else {
        currentUser.following = [pubkey];
      }
    }
    if (currentProfileRelationshipState && !document.getElementById('profileRelationshipsView')?.classList.contains('hidden')) {
      renderProfileRelationshipTab(currentProfileRelationshipState.activeTab);
    } else if (currentProfileViewState?.pubkey === pubkey) {
      renderProfileView();
    }
  } catch (err) {
    console.error('Follow/unfollow failed:', err);
  }
}

// Handle upvote
async function handleUpvote(messageId) {
  if(!isLoggedIn) return;
  try {
    await apiUpvote(messageId);
    if (isThreadView && currentThreadMessageId) {
      const expectedThread = currentThreadMessageId;
      const data = await apiFetchMessage(expectedThread);
      const replies = data.replies || [];
      await hydrateMessageMentions([data.message, ...replies]);
      if (isThreadView && currentThreadMessageId === expectedThread) renderThread(data.message, replies);
    } else {
      loadFeed(true);
    }
  } catch (error) { console.error('Upvote failed:', error); }
}

// Handle downvote
async function handleDownvote(messageId) {
  if(!isLoggedIn) return;
  try {
    await apiDownvote(messageId);
    if (isThreadView && currentThreadMessageId) {
      const expectedThread = currentThreadMessageId;
      const data = await apiFetchMessage(expectedThread);
      const replies = data.replies || [];
      await hydrateMessageMentions([data.message, ...replies]);
      if (isThreadView && currentThreadMessageId === expectedThread) renderThread(data.message, replies);
    } else {
      loadFeed(true);
    }
  } catch (error) { console.error('Downvote failed:', error); }
}

function handleReport(messageId) {
  if (!isLoggedIn) return;
  pendingReportMessageId = messageId;
  document.getElementById('reportReason').value = '';
  showModal('reportModal');
}

async function submitReport(event) {
  event.preventDefault();
  if (!pendingReportMessageId) return;
  const reason = document.getElementById('reportReason').value.trim();
  if (!reason) return;
  try {
    await apiReport(pendingReportMessageId, reason);
    closeModal('reportModal');
    pendingReportMessageId = null;
    showToast('Report sent. Thank you.');
  } catch (error) {
    showError(document.getElementById('reportError'), error.message || 'Could not send report');
  }
}

/*
// Modal functions
function showLoginModal() {
  document.getElementById('loginModal').classList.add('show');
}

function showRegisterModal() {
  document.getElementById('registerModal').classList.add('show');
}

function showActivationModal() {
  document.getElementById('activationModal').classList.add('show');
}

function showLinkAccountModal() {
  document.getElementById('linkAccountModal').classList.add('show');
}
*/

function showModal(modalId) {
  const modal = document.getElementById(modalId);
  if (!modal) return;
  modal.classList.add('show');
  requestAnimationFrame(() => modal.querySelector('input, textarea, button')?.focus());
}

function showLoginModal() { showModal('loginModal'); }
function showActivationModal() { showModal('activationModal'); }

const LEGAL_PAGES = Object.freeze({
  terms: {
    path: '/terms',
    title: 'Terms of Service',
    documentTitle: 'Terms of Service · Kekko',
    markdown: termsMarkdown
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy Policy',
    documentTitle: 'Privacy Policy · Kekko',
    markdown: privacyMarkdown
  }
});

function escapeLegalHtml(value) {
  return String(value).replace(/[&<>"']/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[character]);
}

function renderLegalInline(value) {
  return escapeLegalHtml(value)
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

function legalHeadingId(value) {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function renderLegalMarkdown(markdown) {
  const output = [];
  const paragraphLines = [];
  const listItems = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    const content = paragraphLines.map((line, index) => {
      const separator = index === 0 ? '' : (paragraphLines[index - 1].hardBreak ? '<br>' : ' ');
      return `${separator}${renderLegalInline(line.text)}`;
    }).join('');
    output.push(`<p>${content}</p>`);
    paragraphLines.length = 0;
  };

  const flushList = () => {
    if (!listItems.length) return;
    output.push(`<ul>${listItems.map(item => `<li>${renderLegalInline(item)}</li>`).join('')}</ul>`);
    listItems.length = 0;
  };

  String(markdown || '').replace(/\r\n?/g, '\n').split('\n').forEach(rawLine => {
    const hardBreak = / {2,}$/.test(rawLine);
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    const levelTwoHeading = line.match(/^##\s+(.+)$/);
    const levelOneHeading = line.match(/^#\s+(.+)$/);
    const quote = line.match(/^>\s?(.*)$/);
    const listItem = line.match(/^-\s+(.+)$/);

    if (levelTwoHeading) {
      flushParagraph();
      flushList();
      const heading = levelTwoHeading[1];
      output.push(`<h2 id="${legalHeadingId(heading)}">${renderLegalInline(heading)}</h2>`);
      return;
    }

    if (levelOneHeading) {
      flushParagraph();
      flushList();
      output.push(`<h1>${renderLegalInline(levelOneHeading[1])}</h1>`);
      return;
    }

    if (quote) {
      flushParagraph();
      flushList();
      output.push(`<blockquote><p>${renderLegalInline(quote[1])}</p></blockquote>`);
      return;
    }

    if (listItem) {
      flushParagraph();
      listItems.push(listItem[1]);
      return;
    }

    flushList();
    paragraphLines.push({ text: line, hardBreak });
  });

  flushParagraph();
  flushList();
  return output.join('');
}

function setLegalPageActive(type = null) {
  const legalView = document.getElementById('legalView');
  const legalTitle = document.getElementById('legalTitle') || document.getElementById('legalPageTitle');
  const legalContent = document.getElementById('legalContent');
  const page = type ? LEGAL_PAGES[type] : null;
  const wasActive = document.body.classList.contains('legal-page-active');
  const active = Boolean(page && legalView);

  legalView?.classList.toggle('show', active);
  document.body.classList.toggle('legal-page-active', active);

  if (!active) {
    if (wasActive) document.title = 'Kekko Feed';
    return;
  }

  if (legalTitle) legalTitle.textContent = page.title;
  if (legalContent && legalContent.dataset.legalPage !== type) {
    legalContent.innerHTML = renderLegalMarkdown(page.markdown);
    legalContent.dataset.legalPage = type;
  }
  document.title = page.documentTitle;
  window.scrollTo(0, 0);
}

async function showLegalPage(type, pushHistory = true) {
  const page = LEGAL_PAGES[type];
  if (!page) return;

  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (pushHistory && currentPath !== page.path) {
    history.pushState({ type: 'legal', page: type }, '', page.path);
    return runRouteTransition(() => showLegalPage(type, false));
  }

  invalidateRouteRequests();
  setJoinPageActive(false);
  setAboutPageActive(false);
  setLegalPageActive(type);
}

function setJoinPageActive(active) {
  const joinView = document.getElementById('joinView');
  if (!joinView) return;
  joinView.classList.toggle('show', active);
  document.body.classList.toggle('join-page-active', active);
  document.title = active ? 'Join Kekko' : 'Kekko Feed';
  if (active) {
    window.scrollTo(0, 0);
  }
}

function deactivateStandalonePages() {
  invalidateRouteRequests();
  setJoinPageActive(false);
  setAboutPageActive(false);
  setLegalPageActive(null);
}

function invalidateRouteRequests() {
  activeRouteGeneration += 1;
  feedLoadGeneration += 1;
  searchRequestId += 1;
  hasMore = false;
  isLoading = false;
  isThreadView = false;
  currentThreadMessageId = null;
  currentUserProfile = null;
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = null;
  pagePostMetadataCache.clear();
  drainPageMetadataJobs();
}

async function showJoinPage(pushHistory = true) {
  if (pushHistory && window.location.pathname !== '/join') {
    history.pushState({ type: 'join' }, '', '/join');
    return runRouteTransition(() => showJoinPage(false));
  }
  invalidateRouteRequests();
  setAboutPageActive(false);
  setLegalPageActive(null);
  setJoinPageActive(true);
}

function setAboutPageActive(active) {
  const aboutView = document.getElementById('aboutView');
  if (!aboutView) return;
  aboutView.classList.toggle('show', active);
  document.body.classList.toggle('about-page-active', active);
  document.title = active ? 'About Kekko' : 'Kekko Feed';
  if (active) window.scrollTo(0, 0);
}

async function showAboutPage(pushHistory = true) {
  if (pushHistory && window.location.pathname !== '/about') {
    history.pushState({ type: 'about' }, '', '/about');
    return runRouteTransition(() => showAboutPage(false));
  }
  invalidateRouteRequests();
  setJoinPageActive(false);
  setLegalPageActive(null);
  setAboutPageActive(true);
}

function closeModal(modalId) {
  const modal = document.getElementById(modalId);
  modal?.classList.remove('show');
  
  // Clear form errors
  const errorDivs = document.querySelectorAll('.error-message');
  const successDivs = document.querySelectorAll('.success-message');
  errorDivs.forEach(div => hideError(div));
  successDivs.forEach(div => hideSuccess(div));
}

// Close modal when clicking outside
window.onclick = function(event) {
  if(event.target.classList.contains('modal')) {
    closeModal(event.target.id);
  }
}

// Error/Success message helpers
function showError(element, message) {
  element.textContent = message;
  element.classList.add('show');
}

function hideError(element) {
  element.classList.remove('show');
}

function showSuccess(element, message) {
  element.textContent = message;
  element.classList.add('show');
}

function hideSuccess(element) {
  element.classList.remove('show');
}

// Setup search functionality
function setupSearch() {
  const searchInput = document.getElementById('searchInput');
  if (!searchInput) return;

  searchInput.addEventListener('input', (e) => {
    const query = e.target.value.trim();
    if (searchTimeout) clearTimeout(searchTimeout);
    if (!query) {
      e.target.value = '';
      void clearSearchAndReturn();
      return;
    }
    const isNewSearchRoute = window.location.pathname !== '/search';
    showSearchView(query, searchPageState.activeTab, isNewSearchRoute, true);
    if (query.length < 2) return;
    searchTimeout = setTimeout(() => {
      performSearch(query);
    }, 300);
  });
}

function resetSearchPageState() {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = null;
  searchRequestId += 1;
  searchPageState.query = '';
  searchPageState.loading = false;
  searchPageState.error = '';
  searchPageState.results = { pages: [], people: [], posts: [] };
  document.getElementById('searchResults')?.classList.remove('show');
}

async function clearSearchAndReturn({ closeMobile = false } = {}) {
  const input = document.getElementById('searchInput');
  if (input) input.value = '';
  resetSearchPageState();
  if (closeMobile) setMobileSearchOpen(false);
  if (window.location.pathname !== '/search') return;

  if (history.state?.searchOriginEntry) {
    history.back();
    return;
  }

  const fallbackPath = history.state?.searchOriginPath || '/';
  const fallbackState = history.state?.searchOriginState || { type: 'feed' };
  history.replaceState(fallbackState, '', fallbackPath);
  await runRouteTransition(() => renderRouteFromLocation(), { showLoader: false });
}

async function showSearchView(query = '', tab = 'page', pushHistory = true, skipFetch = false) {
  const normalizedTab = ['page', 'people', 'post'].includes(tab) ? tab : 'page';
  const normalizedQuery = String(query || '').trim();
  const searchPath = `/search?q=${encodeURIComponent(normalizedQuery)}&tab=${encodeURIComponent(normalizedTab)}`;
  if (pushHistory) {
    const searchOriginPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const searchOriginState = history.state;
    history.pushState({
      type: 'search',
      query: normalizedQuery,
      tab: normalizedTab,
      searchOriginEntry: true,
      searchOriginPath,
      searchOriginState
    }, '', searchPath);
    return runRouteTransition(
      () => showSearchView(normalizedQuery, normalizedTab, false, skipFetch),
      { showLoader: !skipFetch && normalizedQuery.length >= 2 }
    );
  }

  deactivateStandalonePages();
  document.body.classList.remove('homepage-layout', 'homepage-feed-expanded');
  const feedEl = document.querySelector('.feed');
  const threadEl = document.getElementById('threadView');
  const profileViewEl = document.getElementById('profileView');
  const searchViewEl = document.getElementById('searchView');
  if (feedEl) feedEl.style.display = 'none';
  if (threadEl) threadEl.style.display = 'none';
  if (profileViewEl) profileViewEl.style.display = 'none';
  if (searchViewEl) searchViewEl.style.display = 'block';
  isThreadView = false;
  currentThreadMessageId = null;
  currentUserProfile = null;
  updateUrlPreviewBar();

  const queryChanged = normalizedQuery !== searchPageState.query;
  searchPageState.query = normalizedQuery;
  searchPageState.activeTab = normalizedTab;
  if (queryChanged) searchPageState.results = { pages: [], people: [], posts: [] };
  if (queryChanged) searchPageState.error = '';
  const input = document.getElementById('searchInput');
  if (input && input.value !== normalizedQuery) input.value = normalizedQuery;
  syncSearchTabs();
  renderSearchViewResults();

  if (window.location.pathname === '/search' && (window.location.search !== searchPath.slice('/search'.length))) {
    history.replaceState({ ...history.state, type: 'search', query: normalizedQuery, tab: normalizedTab }, '', searchPath);
  }
  if (!skipFetch && normalizedQuery.length >= 2 && (queryChanged || searchPageState.loading)) await performSearch(normalizedQuery);
}

function syncSearchTabs() {
  document.querySelectorAll('[data-search-tab]').forEach(button => {
    const active = button.dataset.searchTab === searchPageState.activeTab;
    button.classList.toggle('active', active);
    button.setAttribute('aria-selected', String(active));
  });
}

async function performSearch(query) {
  const requestId = ++searchRequestId;
  searchPageState.loading = true;
  renderSearchViewResults();
  try {
    const data = await apiSearch(query);
    if (requestId !== searchRequestId || query !== searchPageState.query) return;
    const messagesFound = Array.isArray(data.messages) ? data.messages : [];
    const pageCandidates = [
      ...messagesFound,
      ...(Array.isArray(data.channels) ? data.channels : [])
    ];
    const pageMap = new Map();
    pageCandidates.forEach(item => {
      const url = String(item.canonicalUrl || item.url || item.pageUrl || item.urlInfo?.url || '').trim();
      if (!url || pageMap.has(url)) return;
      pageMap.set(url, {
        url,
        title: getPagePreviewTitle(url, item),
        image: getPagePreviewImage(item),
        verified: isPagePreviewVerified(item)
      });
    });
    searchPageState.results = {
      pages: [...pageMap.values()],
      people: Array.isArray(data.users) ? data.users : [],
      posts: messagesFound
    };
    await hydrateMessageMentions(messagesFound);
    if (requestId !== searchRequestId || query !== searchPageState.query) return;
    void hydratePagePostMetadata(messagesFound);
  } catch (error) {
    console.error('Error searching:', error);
    if (requestId === searchRequestId) searchPageState.error = 'Error performing search';
  } finally {
    if (requestId === searchRequestId) {
      searchPageState.loading = false;
      renderSearchViewResults();
    }
  }
}

function renderSearchViewResults() {
  const container = document.getElementById('searchViewResults');
  if (!container) return;
  const query = searchPageState.query;
  if (query.length < 2) {
    container.innerHTML = '<div class="empty-state">Type at least two characters to search.</div>';
    return;
  }
  if (searchPageState.loading) {
    container.innerHTML = '<div class="loading">Searching…</div>';
    return;
  }
  if (searchPageState.error) {
    container.innerHTML = `<div class="empty-state">${escapeHtml(searchPageState.error)}</div>`;
    searchPageState.error = '';
    return;
  }

  const activeTab = searchPageState.activeTab;
  if (activeTab === 'page') {
    const pages = searchPageState.results.pages;
    container.innerHTML = pages.length ? pages.map(page => `
      <button class="search-page-row" type="button" data-search-page-url="${escapeHtml(page.url)}">
        ${page.image ? `<img class="search-result-preview" src="${escapeHtml(page.image)}" alt="">` : '<span class="search-result-preview" aria-hidden="true"></span>'}
        <span class="search-result-copy"><span class="search-result-title">${escapeHtml(page.title)}${page.verified ? '<i class="fa-solid fa-badge-check verified-account-badge" aria-label="Verified website"></i>' : ''}</span><span class="search-result-meta">${escapeHtml(formatPageDisplayUrl(page.url))}</span></span>
      </button>`).join('') : '<div class="empty-state">No pages found.</div>';
    return;
  }
  if (activeTab === 'people') {
    const people = searchPageState.results.people;
    container.innerHTML = people.length ? people.map(person => {
      const identity = getProfileIdentity(person);
      return `<button class="search-person-row" type="button" data-search-person-pubkey="${escapeHtml(person.pubkey || '')}">
        <span class="search-result-avatar" aria-hidden="true">${escapeHtml(identity.initial)}${identity.avatarUrl ? `<img src="${escapeHtml(identity.avatarUrl)}" alt="">` : ''}</span>
        <span class="search-result-copy"><span class="search-result-title">${escapeHtml(identity.primary)}${identity.verified ? '<i class="fa-solid fa-badge-check verified-account-badge" aria-label="Verified username"></i>' : ''}</span><span class="search-result-meta">${escapeHtml(identity.secondary || person.pubkey || '')}</span></span>
      </button>`;
    }).join('') : '<div class="empty-state">No people found.</div>';
    return;
  }

  const posts = searchPageState.results.posts;
  container.innerHTML = posts.length
    ? posts.map(message => createMessageCard(message, { isLoggedIn, currentUser, webPageView: true })).join('')
    : '<div class="empty-state">No posts found.</div>';
}

// Search: Select channel
function searchSelectChannel(channel) {
  document.getElementById('searchInput').value = '';
  setMobileSearchOpen(false);
  //selectChannel(channel);
  showChannelView(channel);
}

// Search: Filter by user
function searchFilterByUser(pubkey) {
  document.getElementById('searchInput').value = '';
  setMobileSearchOpen(false);
  showProfileView(pubkey);
}

/*
// Auto-refresh feed every 30 seconds to fetch new messages / thread replies
setInterval(() => {
  if (isThreadView && currentThreadMessageId) {
    fetchNewThreadReplies();
  } else {
    fetchNewMessages();
    refreshChannels();
  }
}, 30000);
*/

// Set up a websocket connection to listen for new messages in real-time
async function setupWebSocket() {
  const ws = new WebSocketClient();
  await ws.connect();

  ws.on('newMessage', async (message) => {
    // If we're in the thread view and the new message is a reply to the current thread
    if (isThreadView && message.inReplyTo === currentThreadMessageId) {
      const expectedThread = currentThreadMessageId;
      try {
        const data = await apiFetchMessage(expectedThread);
        const replies = data.replies || [];
        await hydrateMessageMentions([data.message, ...replies]);
        if (isThreadView && currentThreadMessageId === expectedThread) renderThread(data.message, replies);
      } catch (err) {
        console.error('Error refreshing thread:', err);
      }

    } else if (!isThreadView) {
      // If we're in the feed view, prepend the new message to the feed
      // if this message is not a reply
      if (!message.inReplyTo) {
        await hydrateMessageMentions([message]);
        if (isThreadView) return;
        messages = [message, ...messages];
        renderMessages();
      } else {
        // Update the thread if the new message is a reply to a message currently in the feed
        const parentMessage = messages.find(m => m.messageId === message.inReplyTo);
        if (parentMessage) {
          parentMessage.replyCount = (parentMessage.replyCount || 0) + 1;
          renderMessages();
        }
      }
    }
  });

  ws.on('voteUpdate', async (messageId) => {
    // If we're in the thread view and the vote update is for the current thread, refresh the thread
    if (isThreadView && messageId === currentThreadMessageId) {
      const expectedThread = currentThreadMessageId;
      try {
        const data = await apiFetchMessage(expectedThread);
        const replies = data.replies || [];
        await hydrateMessageMentions([data.message, ...replies]);
        if (isThreadView && currentThreadMessageId === expectedThread) renderThread(data.message, replies);
      } catch (err) {
        console.error('Error refreshing thread:', err);
      }
    } else if (!isThreadView) {
      // If we're in the feed view, update this single message's up and down vote counts
      const message = messages.find(m => m.messageId === messageId);
      if (message) {
        apiFetchMessage(messageId).then(data => {
          message.upvoteCount = data.message.upvoteCount;
          message.downvoteCount = data.message.downvoteCount;

          // Find the message in the DOM and update the counts without re-rendering the entire feed
          const card = document.querySelector(`.message-card[data-message-id="${CSS.escape(messageId)}"]`);
          if (card) {
            const upvoteSpan   = card.querySelector('[data-action="upvote"] span')   ?? card.querySelectorAll('.vote-display span')[0];
            const downvoteSpan = card.querySelector('[data-action="downvote"] span') ?? card.querySelectorAll('.vote-display span')[1];
            if (upvoteSpan)   upvoteSpan.textContent   = message.upvoteCount   || 0;
            if (downvoteSpan) downvoteSpan.textContent = message.downvoteCount || 0;
          }
        }).catch(err => console.error('Error refreshing message:', err));
      }
    }
  });
}
setupWebSocket();

function setupTheme() {
  const modeButtons = [...document.querySelectorAll('[data-theme-mode]')];
  const media = window.matchMedia('(prefers-color-scheme: light)');
  const apply = value => {
    const theme = ['dark', 'light', 'system'].includes(value) ? value : 'system';
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.resolvedTheme = theme === 'system' ? (media.matches ? 'light' : 'dark') : theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', document.documentElement.dataset.resolvedTheme === 'light' ? '#ffffff' : '#000000');
    modeButtons.forEach(button => button.setAttribute('aria-checked', String(button.dataset.themeMode === theme)));
  };

  apply(localStorage.getItem('kekko-theme') || 'system');
  modeButtons.forEach(button => button.addEventListener('click', () => {
    localStorage.setItem('kekko-theme', button.dataset.themeMode);
    apply(button.dataset.themeMode);
    closeDisplayMenu();
  }));
  media.addEventListener('change', () => {
    if ((localStorage.getItem('kekko-theme') || 'system') === 'system') apply('system');
  });
}

function closeDisplayMenu(exceptMenu = null) {
  document.querySelectorAll('[data-display-menu]').forEach(menu => {
    if (menu === exceptMenu) return;
    menu.classList.add('hidden');
    menu.closest('.display-menu')?.querySelector('[data-display-menu-trigger]')?.setAttribute('aria-expanded', 'false');
  });
}

function syncSortMenu(value) {
  const labels = { new: 'Newest', old: 'Oldest', top: 'Top', following: 'Following' };
  const mode = labels[value] ? value : 'new';
  const label = document.getElementById('sortMenuLabel');
  if (label) label.textContent = labels[mode];
  document.querySelectorAll('[data-sort-mode]').forEach(button => {
    button.setAttribute('aria-checked', String(button.dataset.sortMode === mode));
  });
}

function closeSortMenu() {
  document.getElementById('sortMenu')?.classList.add('hidden');
  document.getElementById('sortMenuBtn')?.setAttribute('aria-expanded', 'false');
}

function setupSortMenu() {
  const select = document.getElementById('sortingMethod');
  syncSortMenu(select?.value || 'new');
  document.querySelectorAll('[data-sort-mode]').forEach(button => {
    button.addEventListener('click', () => {
      if (!select || button.disabled) return;
      select.value = button.dataset.sortMode;
      syncSortMenu(select.value);
      closeSortMenu();
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

function setMobileSearchOpen(open) {
  const navbar = document.querySelector('.navbar');
  const input = document.getElementById('searchInput');
  const results = document.getElementById('searchResults');
  const trigger = document.getElementById('mobileSearchBtn');
  navbar?.classList.toggle('search-open', open);
  trigger?.setAttribute('aria-expanded', String(open));

  if (open) {
    closeDisplayMenu();
    input?.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (input && document.activeElement !== input) input.focus({ preventScroll: true });
    });
  } else {
    results?.classList.remove('show');
  }
}

function showToast(message) {
  const region = document.getElementById('toastRegion');
  if (!region) return;
  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.textContent = message;
  region.appendChild(toast);
  setTimeout(() => toast.remove(), 2800);
}

function closePostMenus(except = null) {
  document.querySelectorAll('.message-options-menu').forEach(menu => {
    if (menu !== except) menu.classList.add('hidden');
  });
  document.querySelectorAll('.message-options-trigger').forEach(button => {
    if (!except || !button.parentElement?.contains(except)) button.setAttribute('aria-expanded', 'false');
  });
}

function openSharePopup(button) {
  const options = document.getElementById('sharePopupOptions');
  if (!options) return;
  options.dataset.shareUrl = button.dataset.shareUrl || '';
  options.dataset.shareTitle = button.dataset.shareTitle || 'Kekko post';
  showModal('shareModal');
}

function openShareDestination(url) {
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (opened) opened.opener = null;
}

async function copyShareUrl(url) {
  try {
    await navigator.clipboard.writeText(url);
    showToast('Link copied');
    return true;
  } catch {
    showToast('Could not copy the link');
    return false;
  }
}

async function handleShareOption(button) {
  const options = button.closest('#sharePopupOptions');
  const url = options?.dataset.shareUrl;
  const title = options?.dataset.shareTitle || 'Kekko post';
  const option = button.dataset.shareOption;
  if (!url || !option) return;

  closeModal('shareModal');
  if (option === 'copy') return copyShareUrl(url);
  if (option === 'native') {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch (error) { if (error?.name !== 'AbortError') await copyShareUrl(url); }
    } else {
      await copyShareUrl(url);
    }
    return;
  }

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  if (option === 'x') openShareDestination(`https://x.com/intent/post?url=${encodedUrl}&text=${encodedTitle}`);
  if (option === 'facebook') openShareDestination(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`);
  if (option === 'whatsapp') openShareDestination(`https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`);
  if (option === 'email') window.location.href = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
}

document.addEventListener('click', event => {
  const authorLink = event.target.closest?.('.message-author');
  if (!authorLink) return;
  const pubkey = authorLink.closest('.message-card')?.dataset.pubkey;
  if (!pubkey) return;
  event.preventDefault();
  showProfileView(pubkey);
}, { capture: true });

// Global click event handler
document.addEventListener('click', async (e) => {
  const target = e.target;

  if (target.closest('[data-retry-feed]')) {
    await loadFeed(messages.length === 0);
    return;
  }

  const displayMenuButton = target.closest('[data-display-menu-trigger]');
  if (displayMenuButton) {
    const menu = displayMenuButton.closest('.display-menu')?.querySelector('[data-display-menu]');
    const willOpen = menu?.classList.contains('hidden');
    closeSortMenu();
    closeDisplayMenu(menu);
    menu?.classList.toggle('hidden', !willOpen);
    displayMenuButton.setAttribute('aria-expanded', String(Boolean(willOpen)));
    return;
  }

  const sortMenuButton = target.closest('#sortMenuBtn');
  if (sortMenuButton) {
    const menu = document.getElementById('sortMenu');
    const willOpen = menu?.classList.contains('hidden');
    closeDisplayMenu();
    menu?.classList.toggle('hidden', !willOpen);
    sortMenuButton.setAttribute('aria-expanded', String(Boolean(willOpen)));
    return;
  }

  if (!target.closest('.display-menu')) closeDisplayMenu();
  if (!target.closest('.feed-sort-menu')) closeSortMenu();

  if (target.closest('#mobileSearchBtn')) {
    setMobileSearchOpen(true);
    return;
  }

  const clearSearchButton = target.closest('#closeSearchBtn, #desktopSearchClearBtn');
  if (clearSearchButton) {
    await clearSearchAndReturn({ closeMobile: clearSearchButton.id === 'closeSearchBtn' });
    return;
  }

  const shareOption = target.closest('.share-option');
  if (shareOption) {
    e.preventDefault();
    await handleShareOption(shareOption);
    return;
  }

  if (!target.closest('.message-options')) closePostMenus();
  
  // Handle modal close buttons
  const closeBtn = target.closest('[data-modal]');
  if(closeBtn) {
    const modalId = closeBtn.dataset.modal;
    closeModal(modalId);
    return;
  }

  if (target.closest('#loginBtn')) {
    showLoginModal();
    return;
  }

  if (target.closest('#registerBtn, #mobileFeedJoinBtn')) {
    showJoinPage(true);
    return;
  }

  const footerPageLink = target.closest('[data-footer-page]');
  if (footerPageLink) {
    e.preventDefault();
    if (footerPageLink.dataset.footerPage === 'about') showAboutPage(true);
    if (footerPageLink.dataset.footerPage === 'join') showJoinPage(true);
    return;
  }

  if (target.closest('.learn-more-link')) {
    e.preventDefault();
    showAboutPage(true);
    return;
  }

  const legalLink = target.closest('[data-legal-page]');
  if (legalLink) {
    e.preventDefault();
    showLegalPage(legalLink.dataset.legalPage, true);
    return;
  }

  if (target.closest('[data-go-home]')) {
    e.preventDefault();
    showChannelView(null, true);
    return;
  }

  const inlinePostUrl = target.closest('.message-inline-url, .embedded-page-preview');
  if (inlinePostUrl) {
    e.preventDefault();
    const pageUrl = inlinePostUrl.dataset.pageUrl;
    if (pageUrl) showPageView(pageUrl);
    return;
  }

  const searchTab = target.closest('[data-search-tab]');
  if (searchTab) {
    await showSearchView(searchPageState.query, searchTab.dataset.searchTab, false, true);
    return;
  }

  const searchPageRow = target.closest('[data-search-page-url]');
  if (searchPageRow) {
    showPageView(searchPageRow.dataset.searchPageUrl);
    setMobileSearchOpen(false);
    return;
  }

  const searchPersonRow = target.closest('[data-search-person-pubkey]');
  if (searchPersonRow) {
    showProfileView(searchPersonRow.dataset.searchPersonPubkey);
    setMobileSearchOpen(false);
    return;
  }

  const profileTab = target.closest('[data-profile-tab]');
  if (profileTab) {
    renderProfileTab(profileTab.dataset.profileTab);
    return;
  }

  const relationshipStat = target.closest('[data-open-relationships]');
  if (relationshipStat) {
    await showProfileRelationships(relationshipStat.dataset.openRelationships);
    return;
  }

  const relationshipTab = target.closest('[data-relationship-tab]');
  if (relationshipTab) {
    selectProfileRelationshipTab(relationshipTab.dataset.relationshipTab);
    return;
  }

  const relationshipBack = target.closest('[data-profile-relationships-back]');
  if (relationshipBack) {
    e.preventDefault();
    if (history.state?.relationshipFromProfile) {
      history.back();
    } else if (currentProfileRelationshipState) {
      const state = currentProfileRelationshipState;
      history.replaceState({ type: 'profile', pubkey: state.pubkey }, '', state.canonicalPath);
      await runRouteTransition(() => showProfileView(state.pubkey, false));
    }
    return;
  }

  const relationshipProfile = target.closest('[data-relationship-profile]');
  if (relationshipProfile) {
    showProfileView(relationshipProfile.dataset.relationshipProfile);
    return;
  }

  if (target.closest('[data-profile-details]')) {
    showProfileDetails();
    return;
  }

  if (target.closest('[data-profile-details-back]')) {
    closeProfileDetails();
    return;
  }

  const copyProfilePubkey = target.closest('[data-copy-profile-pubkey]');
  if (copyProfilePubkey) {
    try {
      await navigator.clipboard.writeText(copyProfilePubkey.dataset.copyProfilePubkey || '');
      showToast('Public key copied');
    } catch {
      showToast('Could not copy the public key');
    }
    return;
  }

  if (target.closest('[data-share-profile]')) {
    await shareCurrentProfile();
    return;
  }

  /*
  // Handle link account button
  if(target.id === 'linkAccount') {
    document.getElementById("createAccountStatus").innerHTML = "";
    showLinkAccountModal();

    // Check if a local account is available
    const storedAccount = await hasStoredAccount();
    if(storedAccount?.hasAccount) {
      document.getElementById("unlockAccountDiv").classList.remove("hidden");
      document.getElementById("unlockAccountPassword").focus();
    } else {
      document.getElementById("createAccountDiv").classList.remove("hidden");
    }

    return;
  }

  // Handle unlock local account button
  if(target.id === 'unlockAccountBtn') {
    await handleLinkedAccountUnlock();
    return;
  }

  // Handle create/restore local account button
  if(target.id === 'createAccountBtn') {
    await handleLinkedAccountCreation();
    return;
  }
  
  // Handle login button
  if(target.id === 'loginBtn') {
    showLoginModal();
    return;
  }
  
  // Handle register button
  if(target.id === 'registerBtn') {
    showRegisterModal();
    return;
  }
  */
  
  // Handle profile dropdown button
  if(target.id === 'profileDropdownBtn') {
    toggleProfileDropdown();
    return;
  }
  
  // Handle preferences button
  if(target.id === 'preferencesBtn') {
    showPreferencesModal();
    return;
  }
  
  // Handle logout buttons
  if(target.id === 'logoutBtn' || target.id === 'navLogoutBtn') {
    handleLogout();
    return;
  }
  
  // Handle channel selection
  const channelItem = target.closest('[data-channel]');
  if(channelItem) {
    const channel = channelItem.dataset.channel;
    //selectChannel(channel === 'all' ? null : channel);
    showChannelView(channel === 'all' ? null : channel);
    return;
  }
  
  // Handle vote and report actions
  const actionBtn = target.closest('[data-action]');
  if(actionBtn) {
    if (actionBtn.disabled) return;
    const action = actionBtn.dataset.action;
    const messageId = actionBtn.dataset.messageId;

    if (action === 'open-share-popup') {
      openSharePopup(actionBtn);
      return;
    }

    if (action === 'toggle-options') {
      const menu = actionBtn.closest('.message-options')?.querySelector('.message-options-menu');
      if (!menu) return;
      const willOpen = menu.classList.contains('hidden');
      closePostMenus(menu);
      menu.classList.toggle('hidden', !willOpen);
      actionBtn.setAttribute('aria-expanded', String(willOpen));
      return;
    }
    
    if(action === 'open-thread') {
      showThreadView(messageId);
      return;
    }
    if(action === 'upvote') {
      handleUpvote(messageId);
    } else if(action === 'downvote') {
      handleDownvote(messageId);
    } else if(action === 'report') {
      handleReport(messageId);
    }
    return;
  }

  // Handle thread back button
  if(target.closest('[data-back-feed]')) {
    e.preventDefault();
    if (['thread', 'profile', 'search'].includes(history.state?.type)) {
      history.back();
    } else {
      showChannelView(null, true);
    }
    return;
  }

  if(target.closest('#backToMainFeed')) {
    if (history.state?.type === 'page') history.back();
    else showChannelView(null, true);
    return;
  }

  // Handle back to thread button
  const backToThreadButton = target.closest('#backToThread');
  if(backToThreadButton) {
    const parentId = backToThreadButton.dataset.parentId;
    if(parentId) {
      showThreadView(parentId);
    }
    return;
  }

  // Handle follow/unfollow button on profile view
  const followBtn = target.closest('.follow-btn');
  if (followBtn) {
    const followPubkey = followBtn.dataset.followPubkey;
    if (followPubkey) {
      await handleFollowUser(followPubkey);
      return;
    }
  }

  if(target.closest('.tagged-user')) {
    // Prevent default link behavior if it's a tagged user link
    e.preventDefault();

    const taggedPubkey = target.closest('.tagged-user').dataset.pubkey;
    if(taggedPubkey) {
      showProfileView(taggedPubkey);
      return;
    }
  }

  // Global-feed cards open their associated Page. Once inside Page, clicking
  // a post opens that post's replies view, matching the extension.
  const messageCard = target.closest('.message-card');
  const pagePreview = target.closest('a.social-card, a.source-address');
  if (messageCard && pagePreview && pagePreview.getAttribute('href')?.startsWith('/p/')) {
    e.preventDefault();
    const pageUrl = pagePreview.dataset.pageUrl || messageCard.dataset.pageUrl;
    if (pageUrl) {
      showPageView(pageUrl);
      return;
    }
  }

  if(messageCard && !target.closest('button') && !target.closest('a')) {
    if (messageCard.closest('#threadContent')) return;
    const messageId = messageCard.dataset.messageId;
    const isPageFeedCard = Boolean(currentUrl && messageCard.closest('#messagesContainer'));
    if (isPageFeedCard && messageId) {
      showThreadView(messageId);
      return;
    }
    const pageUrl = messageCard.dataset.pageUrl;
    if(pageUrl) {
      showPageView(pageUrl);
      return;
    }
  } else if(messageCard && !target.closest('button') && target.closest('a') && target.closest('.tagged-user')) {
    // If the click is on a tagged user within the message card, show that user's profile
    const taggedPubkey = target.closest('.tagged-user').dataset.pubkey;
    if(taggedPubkey) {
      showProfileView(taggedPubkey);
      return;
    }
  }
  
  // Handle search result selection
  const searchItem = target.closest('[data-search-type]');
  if(searchItem) {
    const searchType = searchItem.dataset.searchType;
    const searchValue = searchItem.dataset.searchValue;
    
    if(searchType === 'channel') {
      searchSelectChannel(searchValue);
    } else if(searchType === 'user') {
      searchFilterByUser(searchValue);
    } else if (searchType === 'message') {
      document.getElementById('searchInput').value = '';
      document.getElementById('searchResults').classList.remove('show');
      setMobileSearchOpen(false);
      showThreadView(searchValue);
    }
    return;
  }

  // Handle clicking on the logo class
  if(target.classList.contains('logo') || target.closest('.logo')) {
    showChannelView(null, true);
    return;
  }
});

document.addEventListener('keydown', event => {
  if (event.key === 'Escape') {
    if (document.querySelector('.navbar')?.classList.contains('search-open')) {
      void clearSearchAndReturn({ closeMobile: true });
      document.getElementById('mobileSearchBtn')?.focus();
      return;
    }
    closeDisplayMenu();
    closeSortMenu();
    closePostMenus();
    document.querySelectorAll('.modal.show').forEach(modal => closeModal(modal.id));
    return;
  }

  if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('.message-author')) {
    event.preventDefault();
    const card = event.target.closest('.message-card');
    if (card?.dataset.pubkey) showProfileView(card.dataset.pubkey);
  }
});

// Add an event listener for dropdown changes
document.addEventListener('change', async (e) => {
  if (e.target.id === 'sortingMethod') {
    const newSortingMethod = e.target.value;
    userPreferences.defaultSortingMethod = newSortingMethod;
    syncSortMenu(newSortingMethod);

    // Only save user preferences if user is logged in. Otherwise, just update the local variable and reload the feed with the new sorting method.
    if (isLoggedIn) {
      await apiSavePreferences(userPreferences.verifiedOnly, userPreferences.minScore, newSortingMethod);
    }
    loadFeed(true);
  }
});
